import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatBRL, formatApiError, formatBookingPeriod, formatDateTimeBR, formatDateBR, fileUrl } from "../lib/api";
import { getClientIp } from "../lib/clientIp";
import { toast } from "sonner";
import { Warning, CheckCircle, XCircle, Key, MapPin, WhatsappLogo, ArrowSquareOut, Clock, Truck, House, ChatCircle, ShoppingCart, ArrowRight, X, ArrowsCounterClockwise, Copy, Camera, ArrowUUpLeft, CaretDown } from "@phosphor-icons/react";
import AsaasCardForm from "../components/AsaasCardForm";
import ChatDrawer from "../components/ChatDrawer";
import { useNotifications } from "../lib/notifications";
import { useAppConfig } from "../lib/appConfig";
import { useAuth } from "../lib/auth";
import useBodyScrollLock from "../lib/useBodyScrollLock";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import { processAndUploadImage } from "../lib/imageUpload";
import { isNative } from "../lib/native";
import { PhotoSourceSheet } from "../components/PhotoSourceSheet";
import { OwnerCaucaoClaimModal, RenterCaucaoClaimBanner } from "../components/CaucaoClaimModal";

const STATUS_META = {
  PENDENTE_PAGAMENTO: { label: "Pagamento pendente", color: "bg-slate-100 text-slate-700" },
  AGUARDANDO_ACEITE: { label: "Aguardando confirmação", color: "bg-amber-100 text-amber-700" },
  ACEITA: { label: "Aceita — combine a retirada", color: "bg-blue-100 text-blue-700" },
  RECUSADA: { label: "Recusada", color: "bg-red-100 text-red-700" },
  EM_USO: { label: "Em uso", color: "bg-orange-100 text-orange-700" },
  AGUARDANDO_CONFIRMACAO_DEVOLUCAO: { label: "Aguardando confirmação de devolução", color: "bg-purple-100 text-purple-700" },
  EM_VISTORIA: { label: "Em vistoria — aguardando baixa", color: "bg-sky-100 text-sky-700" },
  CONCLUIDA: { label: "Concluída", color: "bg-green-100 text-green-700" },
  DISPUTA_ABERTA: { label: "Em disputa", color: "bg-red-100 text-red-700" },
  CANCELADO_INATIVIDADE: { label: "Não respondida no prazo", color: "bg-slate-200 text-slate-700" },
  CANCELADO_LOCATARIO: { label: "Pagamento não concluído", color: "bg-slate-200 text-slate-700" },
  CANCELADO_DONO: { label: "Cancelado por falta de estoque", color: "bg-amber-100 text-amber-700" },
  EXPIRADA: { label: "Checkout expirado", color: "bg-slate-200 text-slate-700" },
};

function StatusBadge({ s }) {
  const meta = STATUS_META[s] || STATUS_META.PENDENTE_PAGAMENTO;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>;
}

function formatBR(iso) {
  return formatDateTimeBR(iso) || "—";
}

// Data completa DD/MM/AAAA (formatDateBR só mostra DD/MM)
function formatDateFullBR(d) {
  if (!d) return "";
  try {
    return new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return d; }
}

function DeliveryBadge({ choice, role }) {
  if (!choice) return null;
  const isDeliver = choice === "eu_entrego";
  const Icon = isDeliver ? Truck : House;
  const label = isDeliver
    ? (role === "owner" ? "Você entrega" : "Entrega pelo dono")
    : (role === "owner" ? "Cliente retira" : "Você retira no local");
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
      <Icon size={11} weight="bold" /> {label}
    </span>
  );
}

function whatsappLink(phone, msg) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(msg)}`;
}

function googleMapsLink(a) {
  if (!a) return null;
  const q = `${a.street}, ${a.number} - ${a.neighborhood}, ${a.city} - ${a.uf}, ${a.cep}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function ContactButtons({ phone, address, msg, showMaps = true, bookingId = null }) {
  const wa = whatsappLink(phone, msg);
  const maps = showMaps ? googleMapsLink(address) : null;
  if (!wa && !maps) return null;
  const trackWhatsapp = () => {
    if (bookingId) {
      api.post(`/bookings/${bookingId}/whatsapp-redirect`).catch(() => {});
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {wa && (
        <a href={wa} target="_blank" rel="noreferrer" onClick={trackWhatsapp} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-full text-xs font-bold" data-testid="whatsapp-btn">
          <WhatsappLogo size={14} weight="fill" /> WhatsApp
        </a>
      )}
      {maps && (
        <a href={maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#004E98] text-white rounded-full text-xs font-bold" data-testid="maps-btn">
          <MapPin size={14} weight="fill" /> Ver no Maps
          <ArrowSquareOut size={12} weight="bold" />
        </a>
      )}
    </div>
  );
}

// V7.0: Matriz de endereço por papel + delivery_choice
function AddressBlock({ b, role }) {
  const delivery = (b.delivery_choice || "").toLowerCase();
  const a = b.address;

  // Status antes de ACEITA: só mostra a cidade aprox.
  if (!b.address_revealed) {
    // V7.8: pós-CONCLUIDA/cancelado/expirada — aluguel finalizado, esconde alerta completamente
    const finalStatuses = ["CONCLUIDA", "CANCELADO_PELO_LOCATARIO", "CANCELADO_LOCATARIO", "CANCELADO_DONO", "CANCELADO_INATIVIDADE", "RECUSADA", "EXPIRADA"];
    if (finalStatuses.includes(b.status_locacao)) {
      return null;
    }
    return (
      <div className="text-[11px] text-slate-600 bg-slate-50 rounded-lg p-2.5 border border-slate-200" data-testid="address-hidden">
        <div className="font-bold text-slate-700">🔒 Endereço completo após o aceite</div>
        {b.city_hint && (
          <div className="mt-1">📍 Cidade: <b>{b.city_hint}</b></div>
        )}
        {b.distance_km != null && (
          <div>🚗 Aproximadamente <b>{b.distance_km} km</b> de você</div>
        )}
        <div className="text-[10px] text-slate-500 italic mt-1">Por segurança, rua e número só aparecem depois que o aluguel for aceito.</div>
      </div>
    );
  }

  // Locação 'eu_entrego' (dono leva até o cliente)
  if (delivery === "eu_entrego") {
    if (role === "owner") {
      if (!a) return null;
      return (
        <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`address-${b.id}`}>
          <div className="font-bold mb-1 text-amber-900">🚚 O cliente solicitou entrega no endereço:</div>
          <div>📍 {a.street}, {a.number} {a.complement && `- ${a.complement}`}</div>
          <div>{a.neighborhood} · {a.city} - {a.uf} · CEP {a.cep}</div>
          {(b.distance_km != null || b.valor_frete > 0) && (
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-semibold text-amber-900" data-testid={`delivery-frete-info-${b.id}`}>
              {b.distance_km != null && <span>🚗 Distância: ~{b.distance_km} km</span>}
              <span>💰 Frete: {b.valor_frete > 0 ? formatBRL(b.valor_frete) : "grátis"}</span>
            </div>
          )}
          {b.delivery_notes && (
            <div className="mt-2 bg-white border border-amber-300 rounded-md p-2 text-amber-900" data-testid={`delivery-notes-${b.id}`}>
              <b>📝 Nota do cliente:</b> {b.delivery_notes}
            </div>
          )}
          <div className="mt-1">📱 Locatário: {b.user_phone}</div>
          <ContactButtons phone={b.user_phone} address={a} msg={`Olá ${b.user_name}, sobre a entrega de ${b.listing_title}...`} bookingId={b.id} />
        </div>
      );
    }
    // Cliente vê só confirmação textual (sem mapa)
    return (
      <div className="text-xs text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid={`address-${b.id}`}>
        <div className="font-bold mb-1 text-emerald-900">🚚 O proprietário irá entregar o item no seu endereço</div>
        {a && (
          <div className="mt-1 text-[11px] text-slate-600">
            📍 {a.street}, {a.number} · {a.neighborhood}, {a.city}
          </div>
        )}
        {(b.distance_km != null || b.valor_frete > 0) && (
          <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] font-semibold text-emerald-900" data-testid={`renter-frete-info-${b.id}`}>
            {b.distance_km != null && <span>🚗 ~{b.distance_km} km</span>}
            <span>💰 Frete: {b.valor_frete > 0 ? formatBRL(b.valor_frete) : "grátis"}</span>
          </div>
        )}
        {b.delivery_notes && (
          <div className="mt-1 text-[11px] text-emerald-800 italic" data-testid={`delivery-notes-${b.id}`}>
            Sua nota: "{b.delivery_notes}"
          </div>
        )}
        <div className="mt-1.5 text-[11px]">📱 Dono: {b.owner_phone}</div>
        <ContactButtons phone={b.owner_phone} address={null} msg={`Olá ${b.owner_name}, sobre a entrega de ${b.listing_title}...`} showMaps={false} bookingId={b.id} />
      </div>
    );
  }

  // Locação 'locatario_busca' (cliente vai até o dono)
  if (delivery === "locatario_busca") {
    if (role === "renter") {
      if (!a) return null;
      return (
        <div className="text-xs text-slate-700 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid={`address-${b.id}`}>
          <div className="font-bold mb-1 text-blue-900">📦 Local de Retirada</div>
          <div>📍 {a.street}, {a.number} {a.complement && `- ${a.complement}`}</div>
          <div>{a.neighborhood} · {a.city} - {a.uf} · CEP {a.cep}</div>
          {/* V7.7: foto da fachada cadastrada pelo dono — ajuda o cliente a encontrar */}
          {a.photo && (
            <div className="mt-2" data-testid={`address-photo-${b.id}`}>
              <div className="text-[10px] text-slate-500 mb-1 font-bold uppercase tracking-wider">📸 Fachada do local</div>
              <a href={fileUrl(a.photo)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-blue-200 hover:opacity-90 transition">
                <img src={fileUrl(a.photo)} alt="Fachada do local de retirada" className="w-full max-h-48 object-cover" loading="lazy" />
              </a>
            </div>
          )}
          <div className="mt-1">📱 Dono: {b.owner_phone}</div>
          <ContactButtons phone={b.owner_phone} address={a} msg={`Olá ${b.owner_name}, sou ${b.user_name}, sobre o aluguel de ${b.listing_title}...`} bookingId={b.id} />
        </div>
      );
    }
    // Dono vê só aviso
    return (
      <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`address-${b.id}`}>
        <div className="font-bold mb-1">📦 Retirada no seu endereço</div>
        <div>O cliente virá buscar o equipamento no seu endereço cadastrado.</div>
        <div className="mt-1.5 text-[11px]">📱 Locatário: {b.user_phone}</div>
        <ContactButtons phone={b.user_phone} address={null} msg={`Olá ${b.user_name}, sobre a retirada de ${b.listing_title}...`} showMaps={false} bookingId={b.id} />
      </div>
    );
  }

  // Fallback legado
  if (!a) return null;
  return (
    <div className="text-xs text-slate-700 bg-slate-50 rounded-lg p-3" data-testid={`address-${b.id}`}>
      <div className="font-bold mb-1">{a.label}</div>
      <div>📍 {a.street}, {a.number} {a.complement && `- ${a.complement}`}</div>
      <div>{a.neighborhood} · {a.city} - {a.uf} · CEP {a.cep}</div>
    </div>
  );
}

// V13.x: detalhamento colapsável do valor (visão do DONO) — diárias × frete × comissão
function OwnerPaymentBreakdown({ b }) {
  const [open, setOpen] = useState(false);
  const gross = b.valor_diarias_gross ?? b.valor_diarias ?? 0;
  const discount = b.discount_amount || 0;
  const frete = b.valor_frete || 0;
  const locadorFee = b.locador_fee || 0;
  const ownerNet = b.owner_net ?? 0;
  const units = b.time_units || b.days || 1;
  const qty = b.quantity || 1;
  const isMensal = b.tipo_cobranca === "MENSAL";
  const unitWord = isMensal ? (units > 1 ? "meses" : "mês") : (units > 1 ? "diárias" : "diária");
  const unitPrice = units && qty ? gross / (units * qty) : null;
  const diariasDesc = `${units} ${unitWord}${qty > 1 ? ` × ${qty}un` : ""}${unitPrice != null ? ` × ${formatBRL(unitPrice)}` : ""}`;
  return (
    <div className="border-t border-slate-100 pt-2" data-testid={`owner-breakdown-${b.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-xs font-semibold text-slate-600"
        data-testid={`toggle-owner-breakdown-${b.id}`}
      >
        <span>Detalhes do valor</span>
        <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-500">
          {open ? "Ocultar" : "Ver"}
          <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-1 text-xs" data-testid={`owner-breakdown-body-${b.id}`}>
          <div className="flex justify-between gap-2">
            <span className="text-slate-600">Diárias ({diariasDesc})</span>
            <span className="font-semibold">{formatBRL(gross)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between gap-2">
              <span className="text-slate-600">Desconto{b.discount_label ? ` (${b.discount_label})` : ""}</span>
              <span className="font-semibold text-emerald-700">− {formatBRL(discount)}</span>
            </div>
          )}
          {frete > 0 && (
            <div className="flex justify-between gap-2" data-testid={`owner-breakdown-frete-${b.id}`}>
              <span className="text-slate-600">Frete (entrega)</span>
              <span className="font-semibold">+ {formatBRL(frete)}</span>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-slate-600">Comissão AlugaJá</span>
            <span className="font-semibold text-red-600">− {formatBRL(locadorFee)}</span>
          </div>
          <div className="flex justify-between gap-2 font-heading font-black border-t border-slate-200 pt-1 mt-1">
            <span>Você recebe</span>
            <span className="text-green-700" data-testid={`owner-breakdown-net-${b.id}`}>{formatBRL(ownerNet)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// V7.0: Card visto pelo DONO (locador)
function RentalCard({ b, refresh, autoOpenChat }) {
  const cfg = useAppConfig();
  const { user } = useAuth();
  const [tokenInput, setTokenInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // V7.3: consentimento do dono ao confirmar devolução (substitui window.confirm())
  const [returnConsent, setReturnConsent] = useState(false);
  const [showEarlyReturnDialog, setShowEarlyReturnDialog] = useState(false);
  const [earlyActivation, setEarlyActivation] = useState(null);
  // V7.5: recusa de devolução (abre disputa)
  const [showRefuseReturnDialog, setShowRefuseReturnDialog] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const navigate = useNavigate();
  // V17: vistoria — reportar divergência
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [inspReason, setInspReason] = useState("");
  const [inspDesc, setInspDesc] = useState("");
  const [inspAmount, setInspAmount] = useState("");
  // V17.9: Solicitar débito de caução (dono)
  const [showCaucaoClaim, setShowCaucaoClaim] = useState(false);
  const [caucaoClaim, setCaucaoClaim] = useState(null);
  // V9.3: disputa pós-conclusão — apenas o DONO pode abrir (quem devolveu não)
  const [showDispute, setShowDispute] = useState(false);
  const [showRateRenter, setShowRateRenter] = useState(false);
  const completedAt = b.timestamp_devolucao ? new Date(b.timestamp_devolucao) : null;
  const hoursSinceComplete = completedAt ? (Date.now() - completedAt.getTime()) / 3600000 : null;
  const canDispute = b.status_locacao === "CONCLUIDA" && hoursSinceComplete != null && hoursSinceComplete <= 48;
  // V10.4: janela de cortesia de 1h após CONCLUÍDA — mantém chat/contato/endereço visíveis
  const inCompletionGrace = b.status_locacao === "CONCLUIDA" && hoursSinceComplete != null && hoursSinceComplete <= 1;
  const chatBaseAllowed = ["ACEITA", "EM_USO"].includes(b.status_locacao) || inCompletionGrace;
  // V7.7: pós-aceite até em-uso o chat está visível. V10.4: + 1h após concluir
  const canChat = chatBaseAllowed;
  const chatReadOnly = false;
  const { byBooking } = useNotifications();
  const unread = byBooking?.[b.id] || 0;

  // Auto-open chat se acionado via deep-link
  useEffect(() => {
    if (autoOpenChat && canChat) setShowChat(true);
  }, [autoOpenChat, canChat]);

  // V17.9: carrega solicitação de caução (se existir)
  const canRequestCaucao = b.caucao_required && b.caucao_status === "held"
    && ["AGUARDANDO_CONFIRMACAO_DEVOLUCAO", "EM_VISTORIA", "CONCLUIDA"].includes(b.status_locacao);
  const hasClaimFlag = ["pending_renter", "pending_admin", "accepted_and_charged", "auto_charged", "resolved_owner", "resolved_renter"].includes(b.caucao_claim_status);
  useEffect(() => {
    if (!hasClaimFlag) { setCaucaoClaim(null); return; }
    let cancel = false;
    api.get(`/bookings/${b.id}/caucao-claim`).then((r) => { if (!cancel) setCaucaoClaim(r.data); }).catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id, b.caucao_claim_status]);

  const accept = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/accept`); toast.success("Reserva aceita!"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const refuse = async () => {
    if (!window.confirm("Recusar essa reserva?")) return;
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/refuse`); toast.success("Reserva recusada."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // V12.2: dono cancela por falta de estoque ("Não tenho o equipamento")
  const ownerUnavailable = async () => {
    const used = user?.owner_cancel_count || 0;       // já usados nos últimos 6 meses
    const nth = Math.min(used + 1, 3);                 // esta solicitação (de 3)
    const willFine = used >= 2;                        // 1ª e 2ª isentas; 3ª+ tem multa
    const msg =
      "⚠️ Use isto APENAS se você aceitou a locação por engano e realmente NÃO tem este equipamento disponível.\n\n" +
      "Não é um cancelamento comum — é o registro de um erro de estoque.\n\n" +
      `Esta seria sua ${nth}ª solicitação de 3 (sem estoque) nos últimos 6 meses.\n` +
      (willFine
        ? "🚫 Como você já usou as 2 isentas, ESTA terá MULTA de R$ 20,00 + o custo do estorno, descontada do seu próximo repasse."
        : "As 2 primeiras a cada 6 meses são isentas; a partir da 3ª há multa de R$ 20,00 + custo do estorno.") +
      "\n\nO locatário será reembolsado integralmente. Deseja confirmar?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/owner-unavailable`, {});
      if (data.first_free) {
        toast.warning("Primeiro cancelamento por falta de estoque é isento. Nos próximos, será cobrada multa. Pause anúncios sem estoque!", { duration: 9000 });
      } else {
        toast.error(`Cancelamento registrado. Multa de ${formatBRL(data.debt_added || 0)} será descontada do seu próximo repasse.`, { duration: 9000 });
      }
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // V18.5: dono OU locatário reclama no-show. Janela: pickup + max(2h, 5% duração) até pickup+48h.
  // Backend valida a janela; frontend só evita clique inútil ocultando o botão fora dela.
  const noShowWindow = (() => {
    if (b.no_show_claimed_by) return { open: false, in_grace: true, claimed_by: b.no_show_claimed_by };
    if (!b.pickup_dt_utc) return { open: false };
    const pickupMs = new Date(b.pickup_dt_utc).getTime();
    const endMs = b.return_dt_prevista_utc ? new Date(b.return_dt_prevista_utc).getTime() : pickupMs + 24 * 3600 * 1000;
    const totalH = Math.max(1, (endMs - pickupMs) / 3600000);
    const startOffsetH = Math.max(2, 0.05 * totalH);
    const openAt = pickupMs + startOffsetH * 3600000;
    const closeAt = pickupMs + 48 * 3600000;
    const nowMs = Date.now();
    return { open: nowMs >= openAt && nowMs < closeAt, before: nowMs < openAt, opens_at: openAt, closes_at: closeAt };
  })();

  const reportNoShow = async () => {
    if (!window.confirm("Confirma que o cliente NÃO retirou o item?\n\nO cliente será notificado e terá +1 hora para retirar. Após esse prazo, será cobrada 1 diária de taxa de não-comparecimento e o restante será estornado ao cliente (menos taxas do app).")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/no-show/claim`, {});
      toast.success("Reclamação registrada. O cliente foi notificado e tem +1 hora para retirar.", { duration: 8000 });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // V7.5: locador recusa a devolução marcada pelo locatário → abre disputa
  const submitRefuseReturn = async () => {
    const reason = refuseReason.trim();
    if (reason.length < 5) { toast.error("Descreva o problema com pelo menos 5 caracteres"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/refuse-return`, { reason });
      toast.success("Disputa aberta. Use o chat para resolver com o locatário.");
      setShowRefuseReturnDialog(false);
      setRefuseReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const activate = async (confirmEarly = false) => {
    if (tokenInput.length !== 4) { toast.error("Digite os 4 dígitos"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/activate`, { token: tokenInput, confirm_early: confirmEarly });
      toast.success(confirmEarly ? "Entrega antecipada — datas remarcadas e locação em uso!" : "Entrega confirmada — locação em uso!");
      refresh();
    }
    catch (e) {
      const detail = e.response?.data?.detail;
      // Issue 9: ativação antes da data de início → pede confirmação para antecipar/remarcar
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "early_activation") {
        setEarlyActivation(detail);
        setBusy(false);
        return;
      }
      toast.error(formatApiError(detail));
    }
    finally { setBusy(false); }
  };
  const complete = async () => {
    // V7.3: aviso de devolução antecipada via DIALOG estilizado (não mais window.confirm)
    const prevista = b.timestamp_devolucao_prevista ? new Date(b.timestamp_devolucao_prevista) : null;
    const now = new Date();
    const isEarly = prevista && prevista > now;
    const isAwaiting = b.status_locacao === "AGUARDANDO_CONFIRMACAO_DEVOLUCAO";
    if (isAwaiting) {
      if (!returnConsent) {
        toast.warning("Marque a caixa de confirmação antes de continuar");
        return;
      }
      // segue
    } else if (isEarly) {
      // abre modal de devolução antecipada — usuário aceita lá
      setShowEarlyReturnDialog(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/complete`);
      if (data?.status_locacao === "EM_VISTORIA") {
        toast.success("Devolução recebida! O item está em vistoria — dê a baixa final quando conferir tudo.", { duration: 8000 });
      } else {
        toast.success("Locação concluída!");
      }
      refresh();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmEarlyReturn = async () => {
    setShowEarlyReturnDialog(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/complete`);
      toast.success(data?.status_locacao === "EM_VISTORIA" ? "Devolução recebida — item em vistoria!" : "Locação concluída!");
      refresh();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // V17: baixa da vistoria (ok | report)
  const finalizeInspection = async (action) => {
    if (action === "report") {
      if (!inspReason) { toast.error("Selecione o motivo da divergência"); return; }
      if (inspDesc.trim().length < 5) { toast.error("Descreva a divergência (mín. 5 caracteres)"); return; }
    }
    setBusy(true);
    try {
      const body = action === "ok"
        ? { action: "ok" }
        : { action: "report", reason_code: inspReason, description: inspDesc.trim(), amount: parseFloat(inspAmount || 0) || 0 };
      const { data } = await api.post(`/bookings/${b.id}/finalize-inspection`, body);
      if (action === "ok") {
        toast.success(b.caucao_required ? "Locação finalizada — caução do cliente liberada!" : "Locação finalizada sem problemas!");
      } else {
        toast.success("Divergência reportada. Acompanhe na Central de Disputas.");
      }
      setShowInspectionDialog(false);
      refresh();
      if (action === "report" && data?.dispute_id) navigate(`/disputas/${data.dispute_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // V7.7: dados sensíveis (endereço, chat, contato, CPF) visíveis durante ACEITA/EM_USO
  // V10.4: + janela de cortesia de 1h após CONCLUÍDA (atraso na devolução)
  const accepted = ["ACEITA", "EM_USO"].includes(b.status_locacao) || inCompletionGrace;

  return (
    <div className="aj-card p-4 space-y-3" data-testid={`rental-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base leading-tight">{b.listing_title}</div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="period">{formatBookingPeriod(b)}</div>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            {b.user_id ? (
              <a href={`#/u/${b.user_id}`} className="text-xs text-[#004E98] font-semibold hover:underline" data-testid={`renter-profile-link-${b.id}`}>Locatário: <strong>{b.user_name}</strong></a>
            ) : (
              <span className="text-xs text-slate-600">Locatário: <strong>{b.user_name}</strong></span>
            )}
            {b.renter_rating && (
              b.renter_rating.count > 0 ? (
                <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full" data-testid={`renter-rating-${b.id}`}>
                  <span className="text-amber-500">★</span>{b.renter_rating.avg.toFixed(1)}
                  <span className="text-amber-400/80 font-medium">({b.renter_rating.count})</span>
                </span>
              ) : (
                <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid={`renter-rating-${b.id}`}>Novo locatário</span>
              )
            )}
            <DeliveryBadge choice={b.delivery_choice} role="owner" />
          </div>
          {/* V7.7: metadados do pedido — quantidade + CPF (após aceite) */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px]" data-testid={`rental-meta-${b.id}`}>
            {(b.quantity > 1) && (
              <span className="text-slate-700"><b>Quantidade:</b> {b.quantity}un</span>
            )}
            {b.user_cpf ? (
              <span className="text-slate-700" data-testid={`rental-cpf-${b.id}`}>
                <b>{(b.user_doc_type || "CPF").toUpperCase() === "CNPJ" ? "CNPJ" : "CPF"}:</b>{" "}
                {(b.user_doc_type || "").toLowerCase() === "cnpj"
                  ? (b.user_cpf || "").replace(/\D/g, "").replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
                  : (b.user_cpf || "").replace(/\D/g, "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}
              </span>
            ) : (b.user_cpf_masked && (
              <span className="text-slate-500" data-testid={`rental-cpf-masked-${b.id}`}>
                <b>{(b.user_doc_type || "").toLowerCase() === "cnpj" ? "CNPJ" : "CPF"}:</b> {b.user_cpf_masked}
              </span>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-base text-[#004E98]" data-testid={`owner-item-value-${b.id}`}>{formatBRL(b.valor_diarias ?? b.amount)}</div>
          <div className="text-[10px] text-green-700 font-bold">Você recebe: {formatBRL(b.owner_net)}</div>
          <StatusBadge s={b.status_locacao} />
        </div>
      </div>

      <OwnerPaymentBreakdown b={b} />

      {/* V7.7: Histórico de extensão (visão única) */}
      {Array.isArray(b.extensions) && b.extensions.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 text-xs" data-testid={`extension-history-${b.id}`}>
          <div className="font-bold text-purple-900 mb-1 flex items-center gap-1">
            🔁 Extensões aplicadas ({b.extensions.length})
          </div>
          {b.extensions.map((ext, i) => (
            <div key={ext.id || i} className="text-purple-800 text-[11px]">
              • Devolução era <b>{formatDateBR(ext.original_end_date)}</b> → alterado para <b>{formatDateBR(ext.new_end_date)}</b>
              <span className="text-purple-500 text-[10px] ml-1">({formatDateBR(ext.created_at)})</span>
            </div>
          ))}
        </div>
      )}

      {b.status_locacao === "AGUARDANDO_ACEITE" && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3" data-testid="alert-aguardando-aceite">
          <div className="flex items-center gap-2 font-bold text-amber-900 text-sm">
            <Warning size={18} weight="fill" /> Aguardando sua confirmação
          </div>
          <p className="text-xs text-amber-800 mt-1 mb-1">
            ⏱️ Responda o quanto antes para não perder o cliente.
          </p>
          {(() => {
            const exp = computeAcceptanceExpiry(b, cfg.tempo_aceitacao_horas);
            if (!exp || Date.now() >= exp.getTime()) return null;
            return (
              <p className="text-[11px] text-amber-900 font-bold mb-3" data-testid="owner-deadline-msg">
                Você tem <b>{humanRemaining(exp)}</b> para responder (até {formatExpiryShort(exp)}).
              </p>
            );
          })()}
          <div className="flex gap-2">
            <button onClick={accept} disabled={busy} className="btn-primary text-sm py-2 px-4 flex-1" data-testid={`accept-${b.id}`}>
              <CheckCircle size={16} weight="bold" /> Aceitar
            </button>
            <button onClick={refuse} disabled={busy} className="btn-ghost text-sm py-2 px-4 text-red-700" data-testid={`refuse-${b.id}`}>
              <XCircle size={16} weight="bold" /> Recusar
            </button>
          </div>
        </div>
      )}

      {b.status_locacao === "ACEITA" && (
        <div className="bg-[#FF6B35]/10 border-2 border-[#FF6B35] rounded-xl p-3" data-testid="alert-aceita-owner">
          <div className="flex items-center gap-2 font-bold text-[#FF6B35] text-sm">
            <Warning size={18} weight="fill" /> Peça o token de 4 dígitos ao cliente
          </div>
          <p className="text-xs text-slate-700 mt-1 mb-3">
            ⚠️ Digite o token para confirmar a entrega e liberar seu pagamento.
          </p>
          <div className="flex gap-2">
            <input
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="0000"
              className="aj-input text-center text-xl tracking-[0.5em] font-bold"
              data-testid={`token-input-${b.id}`}
            />
            <button onClick={() => activate()} disabled={busy || tokenInput.length !== 4} className="btn-primary px-4" data-testid={`activate-${b.id}`}>
              <Key size={16} weight="bold" />
            </button>
          </div>
          <button onClick={ownerUnavailable} disabled={busy} className="btn-ghost text-xs py-2 px-3 mt-2 w-full text-red-700 border border-red-200 hover:bg-red-50" data-testid={`owner-unavailable-${b.id}`}>
            <XCircle size={14} weight="bold" /> Não tenho o equipamento
          </button>
          {noShowWindow.open && (
            <button onClick={reportNoShow} disabled={busy} className="btn-ghost text-xs py-2 px-3 mt-2 w-full text-amber-800 border border-amber-300 hover:bg-amber-50" data-testid={`no-show-${b.id}`}>
              <Warning size={14} weight="bold" /> Cliente não compareceu / retirou o item
            </button>
          )}
          {noShowWindow.claimed_by && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1.5 mt-2" data-testid={`no-show-in-grace-${b.id}`}>
              <b>Reclamação em andamento</b> — {noShowWindow.claimed_by === "owner" ? "aguardando o cliente retirar" : "o locatário disse que não recebeu o item"}. Após +1h, a locação é liquidada como não-comparecimento.
            </div>
          )}
        </div>
      )}

      {/* V17: complementos + caução — visão do locador */}
      {Array.isArray(b.complements_lines) && b.complements_lines.length > 0 && (
        <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`rental-complements-${b.id}`}>
          <b>Complementos:</b> {b.complements_lines.map((l) => l.kind === "variant" ? `${l.name}: ${l.option}` : `${l.name} × ${l.qty}`).join(" · ")}
        </div>
      )}
      {b.caucao_required && (
        <div className="text-[11px] font-bold rounded-lg px-2 py-1.5 border bg-amber-50 border-amber-200 text-amber-900" data-testid={`rental-caucao-${b.id}`}>
          🛡 Caução de {formatBRL(b.caucao_amount)}: {" "}
          {b.caucao_status === "held" && "retida no cartão do cliente."}
          {b.caucao_status === "released" && "liberada ao cliente."}
          {b.caucao_status === "charged" && `cobrada — R$ ${(b.caucao_charged_amount || 0).toFixed(2)} debitados do cartão do cliente e creditados a você (taxa de gerenciamento da caução de R$ 5,00 já foi descontada no aluguel).`}
          {b.caucao_status === "charging" && "em processamento."}
          {!["held", "released", "charged", "charging"].includes(b.caucao_status) && "pendente."}
          {/* V17.9: status da solicitação de débito */}
          {caucaoClaim?.status === "pending_renter" && (
            <div className="mt-1.5 text-[11px] font-normal text-slate-700" data-testid={`rental-claim-pending-${b.id}`}>
              ⏳ Solicitação enviada — locatário tem até <b>{caucaoClaim.expires_at ? new Date(caucaoClaim.expires_at).toLocaleString("pt-BR") : "48h"}</b> para responder.
            </div>
          )}
          {caucaoClaim?.status === "pending_admin" && (
            <div className="mt-1.5 text-[11px] font-normal text-red-800" data-testid={`rental-claim-contested-${b.id}`}>
              🛡 Locatário contestou — em análise pelo suporte AlugaJá.
            </div>
          )}
          {(caucaoClaim?.status === "accepted_and_charged" || caucaoClaim?.status === "auto_charged" || caucaoClaim?.status === "resolved_owner") && (
            <div className="mt-1.5 text-[11px] font-normal text-emerald-800">
              ✅ Débito efetivado: {formatBRL(caucaoClaim.charged_amount || 0)}.
              {caucaoClaim.status === "auto_charged" && " (auto após 48h sem resposta)"}
              {caucaoClaim.status === "resolved_owner" && " (decisão do suporte)"}
            </div>
          )}
          {caucaoClaim?.status === "resolved_renter" && (
            <div className="mt-1.5 text-[11px] font-normal text-slate-600">
              ℹ️ Suporte decidiu pelo locatário — caução liberada.
            </div>
          )}
          {/* Botão de solicitar débito — só se não há claim ativa */}
          {canRequestCaucao && !caucaoClaim && (
            <div className="mt-2">
              <button onClick={() => setShowCaucaoClaim(true)}
                className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700"
                data-testid={`rental-request-caucao-${b.id}`}>
                🛡 Solicitar débito da caução
              </button>
            </div>
          )}
        </div>
      )}

      {/* V17.9: modal do dono para criar a solicitação */}
      {showCaucaoClaim && (
        <OwnerCaucaoClaimModal
          bookingId={b.id}
          caucaoAmount={b.caucao_amount || 0}
          onClose={() => setShowCaucaoClaim(false)}
          onCreated={() => refresh()}
        />
      )}


      {b.status_locacao === "EM_USO" && b.timestamp_devolucao_prevista && (() => {
        const tol = b.return_tolerance_until ? new Date(b.return_tolerance_until) : null;
        const isLate = tol && Date.now() > tol.getTime();
        const ownerFetch = (b.delivery_choice || "").toLowerCase() === "eu_entrego";
        return (
        <div className={`border rounded-xl p-3 ${isLate ? "bg-red-50 border-red-300" : "bg-orange-50 border-orange-200"}`} data-testid="alert-em-uso">
          <div className={`text-sm font-semibold flex items-center gap-1 ${isLate ? "text-red-900" : "text-orange-900"}`}>
            <Clock size={16} weight="bold" /> {ownerFetch ? "Busque seu item até" : "Previsão de devolução"}: {formatBR(b.timestamp_devolucao_prevista)}
          </div>
          <CountdownTimer target={new Date(b.timestamp_devolucao_prevista)} tolerance={tol} role="owner" testId={`owner-em-uso-countdown-${b.id}`} />
          {isLate && (
            <p className="text-[11px] text-red-800 mt-1" data-testid={`owner-late-warn-${b.id}`}>
              ⚠️ Devolução em atraso. O locatário foi notificado e está <b>bloqueado de novos aluguéis</b> até você confirmar o recebimento. Se o item não voltar, você poderá abrir uma disputa.
            </p>
          )}
          <button onClick={complete} disabled={busy} className="btn-blue text-sm py-2 px-4 mt-2" data-testid={`complete-${b.id}`}>
            <CheckCircle size={16} weight="bold" /> Já recebi o item
          </button>
        </div>
        );
      })()}

      {b.status_locacao === "AGUARDANDO_CONFIRMACAO_DEVOLUCAO" && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-3" data-testid="alert-aguardando-confirmacao">
          <div className="text-sm text-purple-900 font-bold flex items-center gap-1">
            <Warning size={16} weight="fill" /> O locatário marcou como devolvido
          </div>
          <p className="text-xs text-purple-800 mt-1">
            Confira o item e confirme o recebimento.
            {b.return_auto_close_at && (
              <>
                {" "}Sem confirmação até <b>{formatBR(b.return_auto_close_at)}</b>, encerramos automaticamente.
              </>
            )}
          </p>
          {/* V7.3: substitui o pop-up confirm() por um botão estilizado com consentimento claro */}
          <div className="mt-3 bg-white rounded-lg p-3 border border-purple-200">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="confirm-consent-label">
              <input
                type="checkbox"
                checked={returnConsent}
                onChange={(e) => setReturnConsent(e.target.checked)}
                className="mt-1"
                data-testid="confirm-consent-check"
              />
              <span className="text-xs text-slate-700">
                Ao aceitar a devolução, reconheço que <b>já recebi o equipamento/ferramenta de volta em perfeitas condições</b>.
              </span>
            </label>
            <button
              onClick={complete}
              disabled={busy || !returnConsent}
              className="btn-primary text-sm py-2 px-4 mt-2 w-full disabled:opacity-50"
              data-testid={`confirm-return-${b.id}`}
            >
              <CheckCircle size={16} weight="bold" /> Confirmar recebimento
            </button>
            {/* V7.5: Botão Recusar / Reportar Problema → abre disputa */}
            <button
              onClick={() => setShowRefuseReturnDialog(true)}
              disabled={busy}
              className="btn-ghost text-xs py-2 px-3 mt-2 w-full text-red-700 border border-red-200 hover:bg-red-50"
              data-testid={`refuse-return-${b.id}`}
            >
              <XCircle size={14} weight="bold" /> Recusar / Reportar problema
            </button>
          </div>
        </div>
      )}

      {/* V17: EM VISTORIA — dono dá a baixa final ou reporta divergência */}
      {b.status_locacao === "EM_VISTORIA" && (
        <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-3" data-testid="alert-em-vistoria">
          <div className="text-sm text-sky-900 font-bold flex items-center gap-1">
            <Warning size={16} weight="fill" /> Item em vistoria — dê a baixa final
          </div>
          <p className="text-xs text-sky-800 mt-1">
            Confira o equipamento, consumíveis e o material descartado.
            {b.inspection_auto_close_at && (
              <> Sem baixa até <b>{formatBR(b.inspection_auto_close_at)}</b>, finalizamos automaticamente{b.caucao_required ? " e liberamos a caução" : ""}.</>
            )}
          </p>
          {b.caucao_required && b.caucao_status === "held" && (
            <p className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2" data-testid="vistoria-caucao-info">
              🛡 Caução de {formatBRL(b.caucao_amount)} segue retida no cartão do cliente até a baixa.
            </p>
          )}
          <div className="grid grid-cols-1 gap-2 mt-3">
            <button
              onClick={() => finalizeInspection("ok")}
              disabled={busy}
              className="btn-primary text-sm py-2 px-4 w-full disabled:opacity-50"
              data-testid={`inspection-ok-${b.id}`}
            >
              <CheckCircle size={16} weight="bold" /> Finalizar sem problemas
            </button>
            <button
              onClick={() => setShowInspectionDialog(true)}
              disabled={busy}
              className="btn-ghost text-xs py-2 px-3 w-full text-red-700 border border-red-200 hover:bg-red-50"
              data-testid={`inspection-report-${b.id}`}
            >
              <XCircle size={14} weight="bold" /> Reportar divergência / cobrança adicional
            </button>
          </div>
        </div>
      )}

      {/* V17: Dialog de divergência na vistoria */}
      {showInspectionDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowInspectionDialog(false)} data-testid="inspection-report-dialog">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" />
              <h3 className="font-heading font-black text-lg">Reportar divergência</h3>
            </div>
            <p className="text-xs text-slate-600">
              Isso abre uma <b>disputa</b> com o locatário. Anexe fotos e negocie o valor na Central de Disputas.
              {b.caucao_status === "held" && <> Se comprovada, o valor poderá ser <b>debitado da caução retida</b> (R$ {Number(b.caucao_amount || 0).toFixed(2)}).</>}
            </p>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Motivo <span className="text-red-600">*</span></span>
              <select value={inspReason} onChange={(e) => setInspReason(e.target.value)} className="aj-input mt-1" data-testid="inspection-reason-select">
                <option value="">Selecione…</option>
                <option value="variant_mismatch">Material descartado diferente da variante contratada</option>
                <option value="damage">Avaria/Dano no equipamento</option>
                <option value="consumable_extra">Consumível extra utilizado e não pago</option>
                <option value="other">Outra divergência</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Descreva o ocorrido <span className="text-red-600">*</span></span>
              <textarea rows={3} value={inspDesc} onChange={(e) => setInspDesc(e.target.value)}
                placeholder="Ex.: caçamba continha entulho misto, contratado era entulho limpo…"
                className="aj-input mt-1 resize-none" data-testid="inspection-description" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Valor estimado da cobrança (R$) — opcional</span>
              <input type="number" min="0" step="0.01" value={inspAmount} onChange={(e) => setInspAmount(e.target.value)}
                placeholder="0,00" className="aj-input mt-1" data-testid="inspection-amount" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setShowInspectionDialog(false)} className="btn-ghost" data-testid="inspection-cancel">Cancelar</button>
              <button onClick={() => finalizeInspection("report")} disabled={busy || !inspReason || inspDesc.trim().length < 5}
                className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50" data-testid="inspection-submit">
                {busy ? "Enviando..." : "Abrir divergência"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V7.5: Dialog para recusa de devolução (abre disputa) */}
      {showRefuseReturnDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowRefuseReturnDialog(false)} data-testid="refuse-return-dialog">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" />
              <h3 className="font-heading font-black text-lg">Reportar problema na devolução</h3>
            </div>
            <p className="text-sm text-slate-700">
              Isso vai abrir uma <b>disputa</b> com o locatário. O chat continua aberto e nosso suporte será notificado.
            </p>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
                Descreva o problema <span className="text-red-600">*</span>
              </span>
              <textarea
                rows={4}
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                placeholder="Ex.: item não foi devolvido, devolvido com defeito, falta peça, etc."
                className="aj-input mt-1 resize-none"
                data-testid="refuse-return-reason"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { setShowRefuseReturnDialog(false); setRefuseReason(""); }} className="btn-ghost" data-testid="refuse-return-cancel">
                Cancelar
              </button>
              <button onClick={submitRefuseReturn} disabled={busy || refuseReason.trim().length < 5} className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50" data-testid="refuse-return-submit">
                {busy ? "Enviando..." : "Abrir disputa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V7.3: modal estilizado de devolução antecipada (substitui window.confirm) */}
      {showEarlyReturnDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowEarlyReturnDialog(false)} data-testid="early-return-dialog">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-700">
              <Warning size={22} weight="fill" />
              <h3 className="font-heading font-black text-lg">Devolução antecipada</h3>
            </div>
            <p className="text-sm text-slate-700">
              O prazo de uso ainda não terminou — a devolução está prevista para <b>{b.timestamp_devolucao_prevista ? formatBR(b.timestamp_devolucao_prevista) : "—"}</b>.
            </p>
            <p className="text-xs text-slate-600">
              Confirme só se o item <b>já foi devolvido</b>. Ao encerrar agora, você reconhece que recebeu o equipamento de volta em perfeitas condições.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setShowEarlyReturnDialog(false)} className="btn-ghost" data-testid="early-return-cancel">
                Voltar
              </button>
              <button onClick={confirmEarlyReturn} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="early-return-confirm">
                {busy ? "Encerrando..." : "Sim, recebi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {earlyActivation && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setEarlyActivation(null)} data-testid="early-activation-dialog">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-700">
              <Warning size={22} weight="fill" />
              <h3 className="font-heading font-black text-lg">Entrega antecipada?</h3>
            </div>
            <p className="text-sm text-slate-700">{earlyActivation.message}</p>
            {earlyActivation.new_start_date && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="early-activation-dates">
                <div className="text-[11px] uppercase font-bold text-amber-700 mb-1">Datas serão remarcadas</div>
                <div className="flex items-center justify-center gap-2 font-bold text-slate-800">
                  <span className="line-through text-slate-400">{formatDateBR(earlyActivation.start_date)} → {formatDateBR(earlyActivation.end_date)}</span>
                  <span className="text-amber-600">vira</span>
                  <span className="text-[#004E98]">{formatDateBR(earlyActivation.new_start_date)} → {formatDateBR(earlyActivation.new_end_date)}</span>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-600">
              Verificamos o estoque nas novas datas e <b>ambas as partes</b> serão avisadas da alteração.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setEarlyActivation(null)} className="btn-ghost" data-testid="early-activation-cancel">
                Ainda não entreguei
              </button>
              <button onClick={() => { setEarlyActivation(null); activate(true); }} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="early-activation-confirm">
                {busy ? "Antecipando..." : "Sim, antecipar entrega"}
              </button>
            </div>
          </div>
        </div>
      )}

      {accepted && (
        <button
          onClick={() => setShowAddress(!showAddress)}
          className="text-xs text-[#004E98] font-semibold flex items-center gap-1"
          data-testid={`toggle-address-${b.id}`}
        >
          <MapPin size={12} weight="bold" /> {showAddress ? "Ocultar" : "Ver"} dados de contato
        </button>
      )}
      {showAddress && accepted && (
        <AddressBlock b={b} role="owner" />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/itens/${b.listing_id}`} className="btn-ghost text-xs py-1.5 inline-flex">Ver item</Link>
        {canChat && (
          <button
            onClick={() => setShowChat(true)}
            className="relative text-xs py-2 px-4 rounded-full bg-gradient-to-r from-[#004E98] to-[#0066C7] text-white hover:shadow-md font-bold inline-flex items-center gap-1.5 shadow-sm"
            data-testid={`owner-open-chat-${b.id}`}
          >
            <ChatCircle size={14} weight="fill" /> Abrir Chat
            {unread > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white"
                data-testid={`owner-chat-badge-${b.id}`}
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        )}
        {canDispute && (
          <button
            onClick={() => setShowDispute(true)}
            className="text-xs py-1.5 px-3 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 font-bold inline-flex items-center gap-1"
            data-testid={`owner-open-dispute-${b.id}`}
          >
            <Warning size={12} weight="bold" /> Abrir disputa
          </button>
        )}
        {b.status_locacao === "DISPUTA_ABERTA" && (
          <Link to="/disputas" className="text-xs py-1.5 px-3 rounded-full bg-red-600 text-white font-bold inline-flex items-center gap-1" data-testid="dispute-center-link">
            <Warning size={12} weight="bold" /> Abrir Central de Disputas
          </Link>
        )}
        {b.status_locacao === "CONCLUIDA" && (
          <button onClick={() => setShowRateRenter(true)} className="text-xs py-1.5 px-3 rounded-full bg-amber-500 text-white font-bold inline-flex items-center gap-1" data-testid={`rate-renter-btn-${b.id}`}>
            <span className="text-white">★</span> Avaliar locatário
          </button>
        )}
      </div>

      {inCompletionGrace && (
        <p className="text-[11px] text-gray-500 -mt-1" data-testid={`owner-grace-note-${b.id}`}>
          Locação concluída — o contato com o locatário fica disponível por mais 1h, caso a devolução atrase.
        </p>
      )}

      {showChat && <ChatDrawer booking={b} onClose={() => setShowChat(false)} readOnly={chatReadOnly} />}
      {showDispute && <DisputeModal booking={b} onClose={() => setShowDispute(false)} onDone={() => { setShowDispute(false); refresh?.(); }} />}
      {showRateRenter && <RenterReviewModal booking={b} onClose={() => setShowRateRenter(false)} onDone={() => setShowRateRenter(false)} />}
    </div>
  );
}

// V14.4: modal do DONO avaliar o LOCATÁRIO
function RenterReviewModal({ booking, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [already, setAlready] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reviews/renter/can-review/${booking.id}`);
        if (data.already && data.rating) { setRating(data.rating); setAlready(true); }
      } catch { /* noop */ }
    })();
  }, [booking.id]);

  const submit = async () => {
    if (rating < 1) { toast.error("Escolha de 1 a 5 estrelas."); return; }
    setBusy(true);
    try {
      await api.post("/reviews/renter", { booking_id: booking.id, rating, comment });
      toast.success(already ? "Avaliação atualizada!" : "Locatário avaliado! Obrigado.");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9997] flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="renter-review-modal">
      <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 className="font-bold text-lg">Avaliar locatário</h3>
          <p className="text-xs text-slate-500">Como foi a experiência com <b>{booking.user_name}</b> em "{booking.listing_title}"?</p>
        </div>
        <div className="flex items-center justify-center gap-1.5" data-testid="renter-review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}
              className="text-3xl leading-none transition-transform hover:scale-110" data-testid={`renter-star-${n}`}
              style={{ color: (hover || rating) >= n ? "#F59E0B" : "#CBD5E1" }}>★</button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Comentário (opcional): pontualidade, cuidado com o item, comunicação..."
          className="aj-input text-sm w-full" data-testid="renter-review-comment" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-slate-100 font-bold text-slate-700" data-testid="renter-review-cancel">Cancelar</button>
          <button onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-[#004E98] text-white font-bold disabled:opacity-60" data-testid="renter-review-submit">
            {busy ? "Enviando..." : already ? "Atualizar" : "Enviar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatExpiryShort(d) {
  if (!d) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `hoje às ${hh}:${mm}`;
  if (isTomorrow) return `amanhã às ${hh}:${mm}`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// V7.5: humanização de tempo restante ("2h 15min", "45 min", etc.)
function humanRemaining(target) {
  if (!target) return "";
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return "menos de 1min";
  const totalMin = Math.floor(diffMs / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  }
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// V14.6: tempo decorrido (para atrasos)
function humanElapsed(target) {
  if (!target) return "";
  const diffMs = Date.now() - target.getTime();
  if (diffMs <= 0) return "menos de 1min";
  const totalMin = Math.floor(diffMs / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  }
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// V14.6: cronômetro regressivo com cor que muda conforme o prazo acaba (dono e locatário)
function CountdownTimer({ target, tolerance, role, testId }) {
  const [, force] = useState(0);
  useEffect(() => {
    const i = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(i);
  }, []);
  if (!target) return null;
  const now = Date.now();
  const lateRef = tolerance || target;
  const isLate = now > lateRef.getTime();
  const hrs = (target.getTime() - now) / 3600000;
  let theme, hint;
  if (isLate) {
    theme = "bg-red-100 border-red-300 text-red-800";
    hint = role === "owner" ? "🔴 Atrasado — vá buscar / cobre a devolução." : "🔴 Atrasado — devolva agora para evitar restrições.";
  } else if (hrs <= 2) {
    theme = "bg-orange-100 border-orange-300 text-orange-800";
    hint = role === "owner" ? "🟠 Falta pouco — prepare-se para receber/buscar o item." : "🟠 Falta pouco — prepare a devolução.";
  } else if (hrs <= 6) {
    theme = "bg-amber-100 border-amber-300 text-amber-800";
    hint = "🟡 A devolução se aproxima.";
  } else {
    theme = "bg-emerald-100 border-emerald-300 text-emerald-800";
    hint = "🟢 Dentro do prazo.";
  }
  return (
    <div className={`mt-2 rounded-xl border px-3 py-2 ${theme} ${isLate || hrs <= 2 ? "animate-pulse" : ""}`} data-testid={testId}>
      <div className="text-sm font-black flex items-center gap-1.5">
        <Clock size={15} weight="bold" />
        {isLate ? `Atrasado há ${humanElapsed(lateRef)}` : `Tempo restante: ${humanRemaining(target)}`}
      </div>
      <div className="text-[11px] font-semibold mt-0.5">{hint}</div>
    </div>
  );
}


// V7.0: Tempo dinâmico do dono respeitando pausa noturna (22h-07h)
function computeAcceptanceExpiry(b, tempoAceitacaoHoras) {
  const created = b.created_at ? new Date(b.created_at) : null;
  if (!created) return null;
  let cur = new Date(created.getTime());
  let remainingMs = (tempoAceitacaoHoras || 1) * 60 * 60 * 1000;
  let safety = 0;
  while (remainingMs > 0 && safety < 100) {
    safety += 1;
    const h = cur.getHours();
    const inPause = h >= 22 || h < 7;
    if (inPause) {
      const next = new Date(cur);
      next.setHours(7, 0, 0, 0);
      if (h >= 22) next.setDate(next.getDate() + 1);
      cur = next;
      continue;
    }
    const endOfActive = new Date(cur);
    endOfActive.setHours(22, 0, 0, 0);
    const slot = endOfActive.getTime() - cur.getTime();
    const consume = Math.min(slot, remainingMs);
    cur = new Date(cur.getTime() + consume);
    remainingMs -= consume;
  }
  return cur;
}

// V7.0: Card visto pelo LOCATÁRIO
function BookingCard({ b: incomingB, onReload, autoOpenChat }) {
  const cfg = useAppConfig();
  // V7.6: override otimista local — UI muda imediatamente após "Já Devolvi" e outras ações
  const [optimisticStatus, setOptimisticStatus] = useState(null);
  const b = optimisticStatus ? { ...incomingB, status_locacao: optimisticStatus } : incomingB;
  // Reset override quando o servidor confirma o novo status
  useEffect(() => {
    if (optimisticStatus && incomingB.status_locacao === optimisticStatus) setOptimisticStatus(null);
  }, [incomingB.status_locacao, optimisticStatus]);

  // V11.1 (Fase 3): cronômetro vivo enquanto EM_USO (re-render a cada 30s)
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (b.status_locacao !== "EM_USO") return;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [b.status_locacao]);

  // V17.9: carrega solicitação de débito de caução (se existir)
  const [renterCaucaoClaim, setRenterCaucaoClaim] = useState(null);
  const hasClaimFlagR = ["pending_renter", "pending_admin", "accepted_and_charged", "auto_charged", "resolved_owner", "resolved_renter"].includes(b.caucao_claim_status);
  useEffect(() => {
    if (!hasClaimFlagR) { setRenterCaucaoClaim(null); return; }
    let cancel = false;
    api.get(`/bookings/${b.id}/caucao-claim`).then((r) => { if (!cancel) setRenterCaucaoClaim(r.data); }).catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id, b.caucao_claim_status]);


  // BUG FIX V7.0: token só aparece em ACEITA (antes da retirada). Após EM_USO some.
  const showToken = b.status_locacao === "ACEITA";
  // V9.3: o locatário (quem devolveu) NÃO abre disputa — apenas o dono. Botão removido.
  // V7.6: pós-CONCLUIDA o BOTÃO de chat some imediatamente (regra de privacidade)
  // V10.4: janela de cortesia de 1h após CONCLUÍDA — mantém chat/contato visíveis
  const completedAtBk = b.timestamp_devolucao ? new Date(b.timestamp_devolucao) : null;
  const hoursSinceCompleteBk = completedAtBk ? (Date.now() - completedAtBk.getTime()) / 3600000 : null;
  const inCompletionGrace = b.status_locacao === "CONCLUIDA" && hoursSinceCompleteBk != null && hoursSinceCompleteBk <= 1;
  const canChat = ["ACEITA", "EM_USO"].includes(b.status_locacao) || inCompletionGrace;
  const chatReadOnly = false;
  // V7.0: Cancelar é DINÂMICO — só aparece após o prazo do dono ter expirado
  const acceptanceExpiry = computeAcceptanceExpiry(b, cfg.tempo_aceitacao_horas);
  const acceptanceExpired = acceptanceExpiry ? Date.now() >= acceptanceExpiry.getTime() : false;
  const canCancel = (
    (b.status_locacao === "AGUARDANDO_ACEITE" && acceptanceExpired) ||
    b.status_locacao === "PENDENTE_PAGAMENTO"
    // Após ACEITA, locatário NÃO pode mais cancelar (regra V7.0)
  );
  const [showChat, setShowChat] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showSchedPay, setShowSchedPay] = useState(false);
  const isScheduled = b.payment_status === "scheduled";
  const isScheduledFailed = b.payment_status === "scheduled_failed";
  const [showStockDetails, setShowStockDetails] = useState(false);
  const { byBooking } = useNotifications();
  const unread = byBooking?.[b.id] || 0;

  // Auto-open chat se acionado via deep-link
  useEffect(() => {
    if (autoOpenChat && canChat) setShowChat(true);
  }, [autoOpenChat, canChat]);

  // V7.2: Já devolvi (locatário) → AGUARDANDO_CONFIRMACAO_DEVOLUCAO
  const [busy, setBusy] = useState(false);

  // V18.5 — locatário pode reclamar "Não recebi o item" na mesma janela que o dono
  const renterNoShowWindow = (() => {
    if (b.no_show_claimed_by) return { open: false, claimed_by: b.no_show_claimed_by };
    if (b.status_locacao !== "ACEITA" || !b.pickup_dt_utc) return { open: false };
    const pickupMs = new Date(b.pickup_dt_utc).getTime();
    const endMs = b.return_dt_prevista_utc ? new Date(b.return_dt_prevista_utc).getTime() : pickupMs + 24 * 3600 * 1000;
    const totalH = Math.max(1, (endMs - pickupMs) / 3600000);
    const startOffsetH = Math.max(2, 0.05 * totalH);
    const openAt = pickupMs + startOffsetH * 3600000;
    const closeAt = pickupMs + 48 * 3600000;
    const nowMs = Date.now();
    return { open: nowMs >= openAt && nowMs < closeAt };
  })();

  const claimNotReceived = async () => {
    if (!window.confirm("Confirma que você NÃO recebeu o item? O dono será notificado e terá +1 hora para entregar. Após esse prazo, a locação será cancelada e o valor estornado (menos as taxas do app).")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/no-show/claim`, {});
      toast.success("Reclamação registrada. O dono foi avisado e tem +1 hora para regularizar.", { duration: 8000 });
      onReload?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const markReturned = async () => {
    if (!window.confirm("Marcar como devolvido? O dono terá que confirmar o recebimento (ou o sistema encerra automaticamente em 24h).")) return;
    setBusy(true);
    // V7.6: atualização otimista — UI muda na hora
    setOptimisticStatus("AGUARDANDO_CONFIRMACAO_DEVOLUCAO");
    try {
      await api.post(`/bookings/${b.id}/mark-returned`);
      toast.success("Devolução marcada! O dono foi notificado.");
      await onReload?.();
    } catch (e) {
      // Reverte otimismo em caso de erro
      setOptimisticStatus(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const [showExtend, setShowExtend] = useState(false);

  // V7.7: dados sensíveis (endereço, chat, contato, CPF) visíveis durante ACEITA/EM_USO
  // V10.4: + janela de cortesia de 1h após CONCLUÍDA (atraso na devolução)
  const accepted = ["ACEITA", "EM_USO"].includes(b.status_locacao) || inCompletionGrace;

  return (
    <div className="aj-card p-4 space-y-3" data-testid={`booking-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base leading-tight">{b.listing_title}</div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="period">{formatBookingPeriod(b)}</div>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            <span className="text-xs text-slate-600">Dono: <strong>{b.owner_name}</strong></span>
            <DeliveryBadge choice={b.delivery_choice} role="renter" />
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-base text-[#004E98]">{formatBRL(b.amount)}</div>
          <StatusBadge s={b.status_locacao} />
        </div>
      </div>

      {/* V17: complementos escolhidos — visão do locatário */}
      {Array.isArray(b.complements_lines) && b.complements_lines.length > 0 && (
        <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`booking-complements-${b.id}`}>
          <b>Complementos:</b> {b.complements_lines.map((l) => l.kind === "variant" ? `${l.name}: ${l.option}` : `${l.name} × ${l.qty}`).join(" · ")}
        </div>
      )}

      {/* V17: caução — status para o locatário */}
      {b.caucao_required && (
        <div className="text-[11px] font-bold rounded-lg px-2 py-1.5 border bg-amber-50 border-amber-200 text-amber-900" data-testid={`booking-caucao-${b.id}`}>
          🛡 Caução de {formatBRL(b.caucao_amount)}: {" "}
          {b.caucao_status === "held" && "reservada no seu cartão (não é cobrança) — será liberada ao fim da locação."}
          {b.caucao_status === "released" && "liberada no seu cartão. ✅"}
          {b.caucao_status === "charged" && `utilizada no acordo (${formatBRL(b.caucao_charged_amount || 0)}${(b.caucao_change_refunded || 0) > 0 ? ` — troco de ${formatBRL(b.caucao_change_refunded)} estornado` : ""}).`}
          {b.caucao_status === "charging" && "em processamento."}
          {!["held", "released", "charged", "charging"].includes(b.caucao_status) && "pendente."}
          {/* V17.9 — status pós-claim para o locatário */}
          {renterCaucaoClaim?.status === "pending_admin" && (
            <div className="mt-1.5 text-[11px] font-normal text-sky-800">🛡 Sua contestação foi enviada — aguardando análise do suporte AlugaJá.</div>
          )}
          {(renterCaucaoClaim?.status === "accepted_and_charged" || renterCaucaoClaim?.status === "auto_charged" || renterCaucaoClaim?.status === "resolved_owner") && (
            <div className="mt-1.5 text-[11px] font-normal text-red-800">
              Débito de {formatBRL(renterCaucaoClaim.charged_amount || 0)} confirmado.
              {renterCaucaoClaim.status === "auto_charged" && " (auto após 48h sem resposta)"}
            </div>
          )}
          {renterCaucaoClaim?.status === "resolved_renter" && (
            <div className="mt-1.5 text-[11px] font-normal text-emerald-800">✅ Suporte decidiu a seu favor — caução liberada.</div>
          )}
        </div>
      )}

      {/* V17.9 — Banner de solicitação pendente para o locatário */}
      {renterCaucaoClaim?.status === "pending_renter" && (
        <RenterCaucaoClaimBanner
          bookingId={b.id}
          claim={renterCaucaoClaim}
          onDone={() => onReload && onReload()}
        />
      )}

      {/* V17: EM VISTORIA — visão do locatário */}
      {b.status_locacao === "EM_VISTORIA" && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900" data-testid="booking-em-vistoria">
          <div className="font-bold flex items-center gap-1"><Clock size={14} weight="bold" /> Item em vistoria</div>
          <p className="mt-1">
            O locador está conferindo o equipamento{b.caucao_required ? " — sua caução será liberada na baixa final" : ""}.
            {b.inspection_auto_close_at && <> Sem baixa até <b>{formatBR(b.inspection_auto_close_at)}</b>, a locação finaliza automaticamente.</>}
          </p>
        </div>
      )}

      {/* V7.7: Histórico de extensão (visão do locatário) */}
      {Array.isArray(b.extensions) && b.extensions.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 text-xs" data-testid={`booking-extension-history-${b.id}`}>
          <div className="font-bold text-purple-900 mb-1">🔁 Extensões aplicadas ({b.extensions.length})</div>
          {b.extensions.map((ext, i) => (
            <div key={ext.id || i} className="text-purple-800 text-[11px]">
              • Devolução era <b>{formatDateBR(ext.original_end_date)}</b> → alterado para <b>{formatDateBR(ext.new_end_date)}</b>
              <span className="text-purple-500 text-[10px] ml-1">({formatDateBR(ext.created_at)})</span>
            </div>
          ))}
        </div>
      )}

      {b.status_locacao === "AGUARDANDO_ACEITE" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
          ⏳ Aguardando o dono aceitar — prazo até {formatExpiryShort(acceptanceExpiry) || `em ~${cfg.tempo_aceitacao_horas}h`}.
          {acceptanceExpiry && !acceptanceExpired && (
            <div className="text-[11px] mt-1 font-bold" data-testid="acceptance-deadline-msg">
              ⏱️ {b.owner_name || "O locador"} tem {humanRemaining(acceptanceExpiry)} para responder.
            </div>
          )}
          {!acceptanceExpired && (
            <div className="text-[11px] mt-1 opacity-80">Cancelamento ficará disponível depois desse prazo.</div>
          )}
        </div>
      )}

      {isScheduled && b.status_locacao === "AGUARDANDO_ACEITE" && (
        <div className="bg-[#004E98]/10 border border-[#004E98]/30 rounded-xl p-3 text-sm text-[#004E98]" data-testid={`scheduled-note-${b.id}`}>
          🗓️ Reserva confirmada. Você ainda não foi cobrado —
          {b.scheduled_charge_at
            ? <> no dia <b>{new Date(b.scheduled_charge_at).toLocaleDateString("pt-BR")}</b> avisaremos (aqui, push e e-mail) para concluir o pagamento <b>naquele mesmo dia</b>.</>
            : <> faltando 4 dias para a retirada avisaremos para concluir o pagamento.</>}
        </div>
      )}

      {isScheduledFailed && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 text-sm text-red-800" data-testid={`scheduled-failed-${b.id}`}>
          ⚠️ Conclua o pagamento {b.scheduled_charge_deadline ? <b>até hoje ({new Date(b.scheduled_charge_deadline).toLocaleDateString("pt-BR")})</b> : "hoje"} (cartão, PIX ou saldo)
          para manter a reserva — senão ela será cancelada e o item liberado.
          <button
            onClick={() => setShowSchedPay(true)}
            className="btn-primary w-full mt-3"
            data-testid={`scheduled-pay-btn-${b.id}`}
          >
            Pagar agora
          </button>
        </div>
      )}

      {showToken && (
        <div className="bg-[#FF6B35]/10 border-2 border-[#FF6B35] rounded-xl p-3" data-testid="token-alert-locatario">
          <div className="flex items-center gap-2 font-bold text-[#FF6B35] text-sm">
            <Warning size={18} weight="fill" /> Apresente este código ao proprietário
          </div>
          <p className="text-xs text-slate-700 mt-1">
            Mostre este código de 4 dígitos ao dono no momento da retirada. <strong>Não compartilhe antes.</strong>
          </p>
          <div className="mt-3 bg-white rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Seu token</div>
            <div className="font-heading font-black text-3xl tracking-[0.4em] text-[#FF6B35]" data-testid={`token-${b.id}`}>
              {b.token_retirada}
            </div>
          </div>
          {accepted && (
            <div className="mt-3">
              <AddressBlock b={b} role="renter" />
            </div>
          )}
        </div>
      )}

      {/* V7.0: endereço para casos não-token (EM_USO, CONCLUIDA, DISPUTA) */}
      {!showToken && accepted && (
        <AddressBlock b={b} role="renter" />
      )}

      {/* V18.5 — Locatário: reclamar não recebimento (mesma janela do dono) */}
      {renterNoShowWindow.open && (
        <button
          onClick={claimNotReceived}
          disabled={busy}
          className="btn-ghost text-xs py-2 px-3 w-full text-amber-800 border border-amber-300 hover:bg-amber-50 mt-2"
          data-testid={`renter-not-received-${b.id}`}
        >
          <Warning size={14} weight="bold" /> Não recebi / não consegui retirar o item
        </button>
      )}
      {renterNoShowWindow.claimed_by && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1.5 mt-2" data-testid={`renter-no-show-in-grace-${b.id}`}>
          <b>Reclamação em andamento</b> — {renterNoShowWindow.claimed_by === "renter" ? "aguardando o dono entregar" : "o dono disse que você não retirou"}. Após +1h, a locação é liquidada como não-comparecimento.
        </div>
      )}

      {b.status_locacao === "EM_USO" && b.timestamp_devolucao_prevista && (() => {
        const prevista = new Date(b.timestamp_devolucao_prevista);
        const extCutoff = b.extension_cutoff_at
          ? new Date(b.extension_cutoff_at)
          : new Date(prevista.getTime() - 4 * 3600000);
        const canExtend = nowTick < extCutoff.getTime();
        const isLate = nowTick > prevista.getTime();
        const ownerFetch = (b.delivery_choice || "").toLowerCase() === "eu_entrego";
        return (
        <div className={`border rounded-xl p-3 text-sm ${isLate ? "bg-red-50 border-red-300 text-red-800" : "bg-green-50 border-green-200 text-green-800"}`} data-testid="alert-em-uso-locatario">
          <div className="flex items-center gap-1.5 font-semibold">
            <Clock size={16} weight="bold" /> {ownerFetch ? "O locador virá buscar até" : "Devolva até"}: {formatBR(b.timestamp_devolucao_prevista)}
          </div>
          <CountdownTimer target={prevista} tolerance={b.return_tolerance_until ? new Date(b.return_tolerance_until) : prevista} role="renter" testId={isLate ? `em-uso-late-${b.id}` : `em-uso-countdown-${b.id}`} />
          <p className="text-[11px] mt-1 opacity-80">Locação iniciada em: {formatBR(b.timestamp_token_ativacao)}</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              onClick={markReturned}
              disabled={busy}
              className="text-xs py-2 px-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`mark-returned-${b.id}`}
            >
              <ArrowUUpLeft size={14} weight="bold" /> {ownerFetch ? "Marcar pronto p/ retirada" : "Confirmar devolução"}
            </button>
            {canExtend ? (
              <button
                onClick={() => setShowExtend(true)}
                className="text-xs py-2 px-3 rounded-full border-2 border-[#004E98] text-[#004E98] font-bold inline-flex items-center gap-1.5"
                data-testid={`extend-${b.id}`}
              >
                <Clock size={14} weight="bold" /> Estender locação
              </button>
            ) : (
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid={`extend-closed-${b.id}`}>
                <Warning size={12} weight="fill" /> Extensões encerradas (4h antes da devolução)
              </span>
            )}
          </div>
        </div>
        );
      })()}

      {b.status_locacao === "AGUARDANDO_CONFIRMACAO_DEVOLUCAO" && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-3" data-testid="alert-aguardando-confirmacao-locatario">
          <div className="text-sm text-purple-900 font-bold flex items-center gap-1">
            <Clock size={16} weight="bold" /> Aguardando confirmação do dono
          </div>
          <p className="text-xs text-purple-800 mt-1">
            Você marcou como devolvido. O dono tem até <b>{formatBR(b.return_auto_close_at)}</b> para confirmar.
            Após esse prazo, o sistema encerra automaticamente.
          </p>
        </div>
      )}

      {b.status_locacao === "CANCELADO_INATIVIDADE" && (
        <div className="bg-slate-100 border border-slate-300 rounded-xl p-3 text-sm text-slate-700" data-testid="alert-auto-cancel">
          🤖 Reserva cancelada automaticamente pois o dono não respondeu no prazo.
          {b.cancel_reason && <div className="text-[11px] mt-1 opacity-70">Motivo: {b.cancel_reason}</div>}
          <div className="text-[11px] mt-1">
            {b.refund_method === "void"
              ? "O valor estava apenas reservado no seu cartão (não cobrado) — a reserva foi liberada e você não foi cobrado."
              : "O valor foi creditado na sua Carteira AlugaJá e pode ser usado em qualquer aluguel."}
          </div>
        </div>
      )}

      {b.status_locacao === "CANCELADO_LOCATARIO" && (
        <div className="bg-slate-100 border border-slate-300 rounded-xl p-3 text-sm text-slate-700" data-testid="alert-renter-cancel">
          O pagamento não foi concluído, então o agendamento foi cancelado e o estoque liberado. Você pode tentar alugar novamente quando quiser.
        </div>
      )}

      {b.status_locacao === "CANCELADO_DONO" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-sm text-amber-900" data-testid="alert-owner-unavailable">
          <button
            onClick={() => setShowStockDetails((v) => !v)}
            className="w-full flex items-center justify-between gap-2 font-bold text-left"
            data-testid="toggle-stock-details"
          >
            <span className="flex items-center gap-1.5"><Warning size={15} weight="fill" /> Cancelado por falta de estoque</span>
            <span className="text-[11px] font-semibold text-amber-700 shrink-0 inline-flex items-center gap-0.5">
              {showStockDetails ? "Ocultar" : "Ver detalhes"}
              <CaretDown size={12} weight="bold" className={`transition-transform ${showStockDetails ? "rotate-180" : ""}`} />
            </span>
          </button>
          {showStockDetails && (
            <div className="mt-2 space-y-1.5" data-testid="stock-details-body">
              <p>Não se preocupe, o dono já foi notificado para corrigir o estoque, nossa prioridade é manter a plataforma sempre atualizada.</p>
              <div className="text-[11px]">
                {b.refund_method === "void"
                  ? "O valor estava apenas reservado no seu cartão (não cobrado) — a reserva foi liberada e você não foi cobrado."
                  : `${b.cancellation_refund ? formatBRL(b.cancellation_refund) : "O valor"} foi creditado na sua Carteira AlugaJá e pode ser usado em qualquer aluguel.`}
              </div>
            </div>
          )}
        </div>
      )}

      {b.status_locacao === "EXPIRADA" && (
        <div className="bg-slate-100 border border-slate-300 rounded-xl p-3 text-sm text-slate-700" data-testid="alert-expired">
          ⏰ O prazo para finalizar este pagamento expirou. Faça uma nova reserva se ainda tiver interesse.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/itens/${b.listing_id}`} className="btn-ghost text-xs py-1.5 inline-flex">Ver item</Link>
        {canChat && (
          <button
            onClick={() => setShowChat(true)}
            className="relative text-xs py-2 px-4 rounded-full bg-gradient-to-r from-[#004E98] to-[#0066C7] text-white hover:shadow-md font-bold inline-flex items-center gap-1.5 shadow-sm"
            data-testid={`open-chat-${b.id}`}
          >
            <ChatCircle size={14} weight="fill" /> Abrir Chat
            {unread > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white"
                data-testid={`chat-badge-${b.id}`}
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        )}
        {b.status_locacao === "DISPUTA_ABERTA" && (
          <Link to="/disputas" className="text-xs py-1.5 px-3 rounded-full bg-red-600 text-white font-bold inline-flex items-center gap-1" data-testid="dispute-center-link">
            <Warning size={12} weight="bold" /> Abrir Central de Disputas
          </Link>
        )}
        {canCancel && (
          <button
            onClick={() => setShowCancel(true)}
            className="text-xs py-1.5 px-3 rounded-full border-2 border-amber-200 text-amber-700 hover:bg-amber-50 font-bold inline-flex items-center gap-1"
            data-testid={`open-cancel-${b.id}`}
          >
            <XCircle size={12} weight="bold" /> Cancelar
          </button>
        )}
      </div>
      {inCompletionGrace && (
        <p className="text-[11px] text-gray-500 -mt-1" data-testid={`grace-note-${b.id}`}>
          Locação concluída — o contato com o dono fica disponível por mais 1h, caso precise alinhar a devolução.
        </p>
      )}
      {showChat && <ChatDrawer booking={b} onClose={() => setShowChat(false)} readOnly={chatReadOnly} />}
      {showCancel && <CancelModal booking={b} onClose={() => setShowCancel(false)} onDone={() => { setShowCancel(false); onReload?.(); }} />}
      {showSchedPay && (
        <ChangePaymentMethodModal
          bookingId={b.id}
          currentMethod={b.payment_method}
          amount={b.amount}
          onClose={() => setShowSchedPay(false)}
          onSuccess={() => { setShowSchedPay(false); onReload?.(); }}
        />
      )}
      {showExtend && <ExtendModal booking={b} onClose={() => setShowExtend(false)} onDone={() => { setShowExtend(false); onReload?.(); }} />}
    </div>
  );
}

function ExtendModal({ booking, onClose, onDone }) {
  const [newEnd, setNewEnd] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  // V7.7: locatário escolhe método de pagamento na extensão
  const [paymentMethod, setPaymentMethod] = useState(booking.payment_method === "pix" ? "pix" : "card");
  const [walletBalance, setWalletBalance] = useState(null);
  // V9.3: etapas de pagamento (abre a tela de pagamento de verdade)
  const [step, setStep] = useState("form");  // form | pay_card | pay_pix
  const [pixResult, setPixResult] = useState(null);
  const [pixPolls, setPixPolls] = useState(0);

  useEffect(() => {
    api.get("/wallet/me").then((r) => setWalletBalance(r.data?.balance || 0)).catch(() => setWalletBalance(0));
  }, []);

  const minDate = (() => {
    const d = booking.end_date ? new Date(booking.end_date + "T00:00:00") : new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  useEffect(() => {
    if (!newEnd) { setQuote(null); return; }
    api.post(`/bookings/${booking.id}/quote-extension`, null, { params: { new_end_date: newEnd } })
      .then((r) => setQuote(r.data))
      .catch((e) => setQuote({ error: e.response?.data?.detail || "erro" }));
  }, [newEnd, booking.id]);

  // V9.3: polling do PIX da extensão
  useEffect(() => {
    if (!pixResult || pixPolls >= 30) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/bookings/status/${pixResult.session_id}`);
        if (data.payment_status === "paid") {
          toast.success("Pagamento confirmado! Aguardando o dono aceitar a extensão.");
          onDone?.();
          return;
        }
        setPixPolls((p) => p + 1);
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [pixResult, pixPolls, onDone]);

  const extraAmount = quote && !quote.error ? (quote.total_charged ?? quote.extra_amount) : 0;
  const walletInsufficient = paymentMethod === "wallet" && walletBalance != null && walletBalance < extraAmount;

  const requestExtension = async (extra = {}) => {
    return api.post(`/bookings/${booking.id}/request-extension`, {
      new_end_date: newEnd,
      origin_url: window.location.origin,
      payment_method: paymentMethod,
      client_ip: await getClientIp(),
      ...extra,
    });
  };

  // Wallet/PIX entram aqui; cartão coleta os dados primeiro (step pay_card)
  const onPrimary = async () => {
    if (!newEnd) return toast.error("Escolha uma nova data");
    if (paymentMethod === "card") { setStep("pay_card"); return; }
    if (paymentMethod === "wallet" && walletInsufficient) {
      return toast.error(`Saldo insuficiente. Você tem ${formatBRL(walletBalance)} e precisa de ${formatBRL(extraAmount)}.`);
    }
    setBusy(true);
    try {
      const { data } = await requestExtension();
      if (paymentMethod === "pix") {
        const pay = data.payment || {};
        if (pay.qr_code) {
          setPixResult({ qr_code: pay.qr_code, qr_code_base64: pay.qr_code_base64, session_id: data.session_id });
          setStep("pay_pix");
          return;
        }
        toast.error("Não foi possível gerar o PIX. Tente novamente.");
        return;
      }
      // wallet
      toast.success("Extensão paga! Aguardando o dono aceitar.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitCard = async (cardData) => {
    setBusy(true);
    try {
      await requestExtension(cardData);
      toast.success("Pagamento autorizado! Aguardando o dono aceitar a extensão.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  // Tela de QR PIX
  if (step === "pay_pix" && pixResult) {
    return <PixInlineModal pix={pixResult} polls={pixPolls} onClose={() => { setPixResult(null); onDone?.(); }} />;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="extend-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[90dvh] overflow-y-auto" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 className="font-heading font-black text-lg">Estender locação</h3>
          <p className="text-xs text-slate-500">Item: <b>{booking.listing_title}</b></p>
        </div>

        {step === "pay_card" ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900">
              Pague os <b>{formatBRL(extraAmount)}</b> extras. A extensão só vale após o <b>dono aceitar</b>.
            </div>
            <AsaasCardForm
              amount={extraAmount}
              submitting={busy}
              showInstallments={true}
              submitLabel={`Pagar ${formatBRL(extraAmount)}`}
              onSubmit={submitCard}
            />
            <button onClick={() => setStep("form")} className="btn-ghost w-full text-xs" data-testid="extend-back">Voltar</button>
          </>
        ) : (
          <>
            <div className="text-sm text-slate-700">
              Data atual de devolução: <b>{formatDateFullBR(booking.end_date)}{booking.end_time ? ` às ${booking.end_time}` : ""}</b>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Nova data de devolução</span>
              <input
                type="date"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
                min={minDate}
                className="aj-input mt-1"
                data-testid="extend-new-end"
              />
            </label>
            {newEnd && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-900" data-testid="extend-new-date-humanized">
                A nova data de devolução será <b>{formatDateFullBR(newEnd)}{booking.end_time ? ` às ${booking.end_time}` : ""}</b>.
              </div>
            )}
            {quote?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">{quote.error}</div>
            )}
            {quote && !quote.error && (
              <div className="aj-card p-3 bg-slate-50 text-sm space-y-1" data-testid="extend-quote">
                <div className="flex justify-between"><span className="text-slate-600">{quote.tipo_cobranca === "MENSAL" ? "Meses extras" : "Dias extras"}</span><span className="font-semibold">{quote.extra_units}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Quantidade</span><span className="font-semibold">{quote.quantity}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Diárias extras</span><span className="font-semibold">{formatBRL(quote.extra_amount)}</span></div>
                {quote.locatario_fee != null && (
                  <div className="flex justify-between"><span className="text-slate-600">Taxa AlugaJá</span><span className="font-semibold">{formatBRL(quote.locatario_fee)}</span></div>
                )}
                <div className="flex justify-between font-heading font-black border-t border-slate-200 pt-1 mt-1"><span>Total a pagar</span><span className="text-[#004E98]" data-testid="extend-total">{formatBRL(quote.total_charged ?? quote.extra_amount)}</span></div>
                <div className="text-[10px] text-slate-500 mt-1">A extensão é tratada como uma <b>nova venda</b> e precisa do <b>aceite do dono</b> + pagamento.</div>
              </div>
            )}

            {/* V7.7: seletor de método de pagamento na extensão */}
            {quote && !quote.error && (
              <div data-testid="extend-payment-method">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Pagar com</span>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <button type="button" onClick={() => setPaymentMethod("card")} className={`p-2 rounded-xl border-2 text-xs font-bold ${paymentMethod === "card" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`} data-testid="extend-pay-card">
                    Cartão
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("pix")} className={`p-2 rounded-xl border-2 text-xs font-bold ${paymentMethod === "pix" ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`} data-testid="extend-pay-pix">
                    PIX
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("wallet")} className={`p-2 rounded-xl border-2 text-xs font-bold ${paymentMethod === "wallet" ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`} data-testid="extend-pay-wallet">
                    Saldo
                    <div className="text-[9px] font-normal text-slate-500">{walletBalance == null ? "—" : formatBRL(walletBalance)}</div>
                  </button>
                </div>
                {paymentMethod === "wallet" && walletInsufficient && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="extend-wallet-insufficient">
                    Saldo insuficiente. Você tem <b>{formatBRL(walletBalance)}</b>.
                  </div>
                )}
                {paymentMethod === "wallet" && !walletInsufficient && walletBalance != null && (
                  <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="extend-wallet-ok">
                    Pagando com saldo da Carteira — sem taxa.
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button onClick={onClose} className="btn-ghost" data-testid="extend-cancel">Cancelar</button>
              <button onClick={onPrimary} disabled={busy || !newEnd || (quote && quote.error) || walletInsufficient} className="btn-primary disabled:opacity-50" data-testid="extend-submit">
                {busy ? "Processando..." : paymentMethod === "card" ? "Ir para pagamento" : paymentMethod === "pix" ? "Gerar PIX" : "Pagar com saldo"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function CancelModal({ booking, onClose, onDone }) {
  const [quote, setQuote] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post(`/bookings/${booking.id}/quote-cancel`).then((r) => setQuote(r.data)).catch(() => setQuote({ can_cancel: false }));
  }, [booking.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`, { reason });
      toast.success(data.refund_method === "wallet_credit"
        ? `Cancelado. ${data.refund ? formatBRL(data.refund) : "R$ 0"} creditado na sua carteira.`
        : "Cancelado. A reserva do seu cartão foi liberada — você não foi cobrado.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!quote) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="cancel-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <XCircle size={22} weight="fill" className="text-amber-600" />
          <h3 className="font-heading font-black text-lg">Cancelar locação</h3>
        </div>
        {!quote.can_cancel ? (
          <>
            <p className="text-sm text-slate-700">{quote.reason || "Locação não pode mais ser cancelada."}</p>
            <button onClick={onClose} className="btn-ghost w-full" data-testid="cancel-modal-close">Fechar</button>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              Item: <b>{booking.listing_title}</b>
            </p>
            {quote.fee > 0 ? (
              <div className="aj-card p-3 space-y-1 text-sm bg-slate-50">
                <div className="flex justify-between"><span className="text-slate-600">Total pago</span><span className="font-semibold">{formatBRL(booking.amount)}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Taxa de quebra de reserva</span>
                  <span className="font-semibold text-red-600">- {formatBRL(quote.fee)}</span>
                </div>
                <div className="flex justify-between font-heading font-black text-base border-t border-slate-200 pt-1.5 mt-1">
                  <span>{quote.refund_destination === "card" ? "Estorno no cartão" : "Crédito na carteira"}</span>
                  <span className="text-emerald-700">{formatBRL(quote.refund)}</span>
                </div>
              </div>
            ) : (
              <div className="aj-card p-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="cancel-refund-info">
                {quote.refund_text || "Você está dentro da janela de cancelamento gratuito — não será cobrado."}
              </div>
            )}
            {quote.refund_text && (
              <p className="text-[11px] text-slate-500 -mt-1" data-testid="cancel-refund-text">{quote.refund_text}</p>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (opcional)"
              rows={2}
              className="aj-input"
              data-testid="cancel-reason"
            />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={onClose} className="btn-ghost" data-testid="cancel-modal-keep">Manter</button>
              <button onClick={submit} disabled={busy} className="px-3 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50" data-testid="cancel-modal-confirm">
                {busy ? "Cancelando..." : "Confirmar cancelamento"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function DisputeModal({ booking, onClose, onDone }) {
  const nav = useNavigate();
  const [disputeType, setDisputeType] = useState("damage");
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoSheet, setPhotoSheet] = useState(false);
  useBodyScrollLock(true);

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    const up = [];
    for (const file of files) {
      try { const data = await processAndUploadImage(file); up.push(data.path); }
      catch (err) { toast.error(err.message || formatApiError(err.response?.data?.detail)); }
    }
    setPhotos((p) => [...p, ...up]);
    setUploading(false);
  };

  const onFiles = (e) => uploadFiles(Array.from(e.target.files || []));

  // App nativo (iOS/Android): abre o menu padronizado (Câmera/Galeria); no web usa o <input>.
  const captureNative = () => setPhotoSheet(true);

  const submit = async () => {
    if (!reason.trim()) return toast.warning("Informe o motivo");
    if (disputeType === "damage" && photos.length === 0) return toast.warning("Anexe pelo menos 1 foto do estrago.");
    setBusy(true);
    try {
      const { data } = await api.post("/disputes/open", {
        booking_id: booking.id, dispute_type: disputeType, reason: reason.trim(), description: desc.trim(), photos,
      });
      toast.success("Disputa aberta. Acompanhe na Central de Disputas.");
      onDone?.();
      nav(`/disputas/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="open-dispute-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="font-heading font-black text-lg">Abrir disputa</div>
          <p className="text-xs text-slate-500">Item: {booking.listing_title}. Acompanhe o andamento na Central de Disputas.</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setDisputeType("damage")}
            className={`p-2.5 rounded-xl border-2 text-xs font-bold ${disputeType === "damage" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
            data-testid="dispute-type-damage">🛠️ Estragou</button>
          <button type="button" onClick={() => setDisputeType("not_returned")}
            className={`p-2.5 rounded-xl border-2 text-xs font-bold ${disputeType === "not_returned" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
            data-testid="dispute-type-not-returned">🚫 Não devolvido</button>
        </div>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="aj-input" data-testid="dispute-reason-select">
          <option value="">Selecione o motivo...</option>
          {disputeType === "damage" ? (
            <>
              <option>Avaria/dano causado</option>
              <option>Peça quebrada</option>
              <option>Equipamento com defeito após uso</option>
              <option>Outro dano</option>
            </>
          ) : (
            <>
              <option>Não devolveu no prazo</option>
              <option>Sumiu / não responde</option>
              <option>Recusa-se a devolver</option>
            </>
          )}
        </select>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descreva o que aconteceu" rows={3} className="aj-input" data-testid="dispute-desc" />
        {disputeType === "damage" && (
          <div data-testid="dispute-photos-upload">
            <label className="flex items-center gap-2 text-xs font-bold text-[#004E98] cursor-pointer">
              <Camera size={16} weight="bold" /> {uploading ? "Enviando..." : "Anexar fotos do estrago (obrigatório)"}
              <input type="file" accept="image/*" multiple className="hidden" onClick={(e) => { if (isNative()) { e.preventDefault(); captureNative(); } }} onChange={onFiles} data-testid="dispute-photo-input" />
            </label>
            {photos.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto">
                {photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />)}
              </div>
            )}
          </div>
        )}
        <PhotoSourceSheet
          open={photoSheet}
          multiple
          onClose={() => setPhotoSheet(false)}
          onPick={(files) => uploadFiles(files)}
        />
        <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2">
          Ao abrir, você declara que as informações são verdadeiras. Forjar provas/danos pode configurar crime (Art. 171). A AlugaJá apenas facilita o contato entre as partes.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={submit} disabled={busy || !reason} className="btn-primary disabled:opacity-50" data-testid="dispute-submit-btn">
            {busy ? "Enviando..." : "Abrir disputa"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// V7.4: Card de reserva aguardando pagamento (locatário não concluiu o checkout)
function PendingPaymentCard({ b, onAbandon }) {
  const [busy, setBusy] = useState(false);
  const [showChangeMethod, setShowChangeMethod] = useState(false);
  const [pixResult, setPixResult] = useState(null);
  const [pixPolls, setPixPolls] = useState(0);
  const tipoLabel = (b.tipo_cobranca || "DIARIO") === "MENSAL" ? "mês(es)" : "dia(s)";
  const units = b.time_units || b.days || (b.start_date && b.end_date
    ? Math.max(1, Math.round((new Date(b.end_date) - new Date(b.start_date)) / 86400000) + 1)
    : 1);

  // V8.1: polling PIX
  useEffect(() => {
    if (!pixResult || pixPolls >= 30) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/bookings/status/${pixResult.session_id}`);
        if (data.payment_status === "paid") {
          toast.success("Pagamento confirmado!");
          onAbandon?.();  // reload
          return;
        }
        setPixPolls((p) => p + 1);
      } catch { /* keep */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [pixResult, pixPolls, onAbandon]);

  // V7.7+V8.1: retomar checkout PIX inline (gera QR novo)
  const resume = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/resume-checkout`, {
        origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
      // V8.1: PIX inline
      if (data?.qr_code) {
        setPixResult(data);
        return;
      }
      toast.error("Não foi possível retomar — tente novamente");
    } catch (e) {
      const det = e.response?.data?.detail;
      // Se erro indica cartão precisa de token novo, abre modal de troca de método
      if (typeof det === "string" && det.includes("change-payment-method")) {
        setShowChangeMethod(true);
      } else {
        toast.error(formatApiError(det));
      }
    } finally {
      setBusy(false);
    }
  };

  const abandon = async () => {
    if (busy) return;
    const ok = window.confirm("Descartar este checkout pendente? Esta ação libera o estoque para outros.");
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/abandon`);
      toast.success("Checkout descartado.");
      onAbandon?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aj-card p-4 border-2 border-amber-200 bg-amber-50/40" data-testid={`pending-booking-${b.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
          {b.listing_image ? (
            <img
              src={fileUrl ? fileUrl(b.listing_image) : b.listing_image}
              alt={b.listing_title || "Item"}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerText = "📦"; }}
            />
          ) : (
            <span className="text-2xl" aria-hidden>📦</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{b.listing_title}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {formatBookingPeriod(b)}
          </div>
          <div className="text-[11px] text-slate-500">
            {units} {tipoLabel}
            {b.quantity > 1 && ` · ${b.quantity}un`}
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 mt-1.5">
            <Clock size={10} weight="bold" /> Aguardando pagamento
          </span>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-base text-[#FF6B35]">{formatBRL(b.valor_total || b.amount || 0)}</div>
        </div>
      </div>

      <p className="text-[11px] text-slate-600 mt-3 leading-relaxed bg-white rounded-lg p-2 border border-amber-200">
        <Warning size={11} weight="fill" className="inline mr-1 text-amber-600" />
        Você iniciou esta reserva mas não concluiu o pagamento. Retome o checkout para garantir o item.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <button
          onClick={resume}
          disabled={busy}
          className="btn-primary w-full sm:flex-1 justify-center min-w-0"
          data-testid={`pending-resume-${b.id}`}
        >
          <ShoppingCart size={14} weight="bold" />
          <span className="truncate">{busy ? "Abrindo..." : "Retomar pagamento"}</span>
          <ArrowRight size={12} weight="bold" />
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => setShowChangeMethod(true)}
            disabled={busy}
            className="btn-ghost text-xs px-3 py-2 flex-1 sm:flex-none justify-center"
            data-testid={`pending-change-method-${b.id}`}
            title="Trocar PIX ↔ Cartão"
          >
            <ArrowsCounterClockwise size={14} weight="bold" />
            Trocar
          </button>
          <button
            onClick={abandon}
            disabled={busy}
            className="btn-ghost text-xs px-3 py-2 flex-1 sm:flex-none justify-center"
            data-testid={`pending-abandon-${b.id}`}
          >
            <X size={14} weight="bold" /> Descartar
          </button>
        </div>
      </div>

      {showChangeMethod && (
        <ChangePaymentMethodModal
          bookingId={b.id}
          currentMethod={b.payment_method}
          amount={b.amount || b.valor_total || 0}
          onClose={() => setShowChangeMethod(false)}
          onSuccess={(newPixData) => {
            setShowChangeMethod(false);
            if (newPixData?.qr_code) setPixResult({ ...newPixData, session_id: newPixData.session_id });
            else { toast.success("Forma de pagamento trocada!"); onAbandon?.(); }
          }}
        />
      )}

      {pixResult && (
        <PixInlineModal
          pix={pixResult}
          polls={pixPolls}
          onClose={() => { setPixResult(null); onAbandon?.(); }}
        />
      )}
    </div>
  );
}

// V8.5: Modal de mudança de método de pagamento — flowin-page (PIX QR + Cartão Brick)
function ChangePaymentMethodModal({ bookingId, currentMethod, amount, onClose, onSuccess }) {
  const { user } = useAuth();
  const [target, setTarget] = useState(currentMethod === "pix" ? "card" : "pix");
  const [busy, setBusy] = useState(false);
  useBodyScrollLock(true);

  const submitPix = async () => {
    if (busy) return;  // V8.5.6: previne double-click
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/change-payment-method`, { payment_method: "pix" });
      toast.success("Forma de pagamento trocada para PIX! Gerando novo QR Code...");
      onSuccess(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // V8.5.6: erros amigáveis — "já é o método atual", "já trocado", etc.
      if (typeof detail === "string" && (detail.toLowerCase().includes("já é") || detail.toLowerCase().includes("ja e") || detail.toLowerCase().includes("método atual"))) {
        toast.info("PIX já é o método de pagamento atual desta reserva.");
      } else {
        toast.error(formatApiError(detail) || "Não foi possível trocar a forma de pagamento.");
      }
      setBusy(false);
    }
  };

  const submitCard = async (cardData) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/change-payment-method`, {
        payment_method: "card",
        client_ip: await getClientIp(),
        ...cardData,
      });
      toast.success("Pagamento autorizado! Reserva confirmada.");
      onSuccess(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (typeof detail === "string" && detail.toLowerCase().includes("método atual")) {
        toast.info("Cartão já é o método de pagamento atual desta reserva.");
      } else {
        toast.error(formatApiError(detail) || "Pagamento recusado. Tente outro cartão.");
      }
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center pb-safe pt-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="change-payment-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl flex flex-col max-h-[92dvh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 pb-3 border-b border-slate-100 shrink-0">
          <div>
            <div className="font-heading font-black text-lg">Trocar forma de pagamento</div>
            <div className="text-xs text-slate-500">Cancela o método atual e gera um novo</div>
          </div>
          <button onClick={onClose} className="text-slate-500" data-testid="change-payment-close"><X size={20} weight="bold" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="change-payment-body" data-scroll-lock-allow="true">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTarget("pix")}
              className={`p-3 rounded-xl border-2 flex flex-col items-start text-left transition-all ${target === "pix" ? "border-[#004E98] bg-[#004E98]/5 ring-2 ring-[#004E98]/20" : "border-slate-200"}`}
              data-testid="change-method-pix"
            >
              <div className="font-bold text-sm">
                PIX (QR Code)
                {currentMethod === "pix" && <span className="ml-1 text-[9px] text-slate-400 font-normal">· atual (gerar novo)</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setTarget("card")}
              className={`p-3 rounded-xl border-2 flex flex-col items-start text-left transition-all ${target === "card" ? "border-[#004E98] bg-[#004E98]/5 ring-2 ring-[#004E98]/20" : "border-slate-200"}`}
              data-testid="change-method-card"
            >
              <div className="font-bold text-sm">
                Cartão
                {currentMethod === "card" && <span className="ml-1 text-[9px] text-slate-400 font-normal">· atual (gerar novo)</span>}
              </div>
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-[11px] text-amber-900">
            Valor: <b>{formatBRL(amount)}</b> · ao confirmar, o pagamento atual é cancelado.
          </div>

          {target === "pix" ? (
            <button
              onClick={submitPix}
              disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="change-method-submit-pix"
            >
              {busy ? "Gerando QR..." : `Gerar novo QR PIX (${formatBRL(amount)})`}
            </button>
          ) : (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 mt-2">Dados do novo cartão</div>
              <AsaasCardForm
                amount={amount}
                submitting={busy}
                showInstallments={true}
                onSubmit={submitCard}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// V8.1: Modal de QR PIX inline (usado em retomar e mudar método)
function PixInlineModal({ pix, polls, onClose }) {
  const copy = () => {
    if (!pix?.qr_code) return;
    navigator.clipboard.writeText(pix.qr_code);
    toast.success("Código PIX copiado!");
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center pb-safe pt-safe" data-testid="pix-inline-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[92dvh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-black text-lg">Pague com PIX</div>
            <div className="text-xs text-slate-500">Aguardando confirmação automática</div>
          </div>
          <button onClick={onClose} className="text-slate-500" data-testid="pix-inline-close"><X size={20} weight="bold" /></button>
        </div>
        {pix.qr_code_base64 && (
          <div className="flex justify-center bg-white p-3 rounded-xl border border-slate-200">
            <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR PIX" className="w-56 h-56" data-testid="pix-inline-qr" />
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Código copia-e-cola</div>
          <div className="flex gap-2">
            <input readOnly value={pix.qr_code || ""} className="aj-input text-[10px] flex-1 font-mono" data-testid="pix-inline-copypaste" />
            <button onClick={copy} className="btn-primary px-3" data-testid="pix-inline-copy"><Copy size={16} weight="bold" /></button>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900">
          Verificando ({polls}/30) — você pode fechar; te avisamos por e-mail/WhatsApp.
        </div>
      </div>
    </div>,
    document.body
  );
}

// V9.9 (Fase 1): filtros rápidos das abas de locações
const RENTER_FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "aberto", label: "Em Aberto" },
  { key: "em_uso", label: "Em Uso" },
  { key: "finalizados", label: "Finalizados" },
  { key: "disputa", label: "Em Disputa" },
];
const OWNER_FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "solicitacoes", label: "Solicitações" },
  { key: "alugados", label: "Alugados" },
  { key: "retornados", label: "Retornados" },
  { key: "disputas", label: "Disputas Ativas" },
];
const renterMatch = (b, key) => {
  const s = b.status_locacao;
  switch (key) {
    case "aberto": return s === "PENDENTE_PAGAMENTO" || s === "AGUARDANDO_ACEITE";
    case "em_uso": return ["ACEITA", "EM_USO", "AGUARDANDO_CONFIRMACAO_DEVOLUCAO", "EM_VISTORIA"].includes(s);
    case "finalizados": return ["CONCLUIDA", "RECUSADA", "CANCELADO_INATIVIDADE", "CANCELADO_LOCATARIO", "CANCELADO_DONO", "EXPIRADA"].includes(s);
    case "disputa": return s === "DISPUTA_ABERTA";
    default: return true;
  }
};
const ownerMatch = (b, key) => {
  const s = b.status_locacao;
  switch (key) {
    case "solicitacoes": return s === "AGUARDANDO_ACEITE";
    case "alugados": return ["ACEITA", "EM_USO"].includes(s);
    case "retornados": return ["CONCLUIDA", "AGUARDANDO_CONFIRMACAO_DEVOLUCAO", "EM_VISTORIA", "CANCELADO_DONO"].includes(s);
    case "disputas": return s === "DISPUTA_ABERTA";
    default: return true;
  }
};

function FilterChips({ filters, active, onChange, counts }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="filter-chips">
      {filters.map((f) => {
        const c = counts?.[f.key] ?? 0;
        const isActive = active === f.key;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onChange(f.key)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${isActive ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-600"}`}
            data-testid={`filter-chip-${f.key}`}
          >
            {f.label}{c > 0 ? ` (${c})` : ""}
          </button>
        );
      })}
    </div>
  );
}

export default function MyBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [pending, setPending] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "rentals" ? "rentals" : "bookings";
  const [tab, setTab] = useState(initialTab);
  // V9.9 (Fase 1): filtros rápidos por aba
  const [renterFilter, setRenterFilter] = useState(tabParam === "pending" ? "aberto" : "todos");
  const [ownerFilter, setOwnerFilter] = useState("todos");
  const [autoOpenChatId, setAutoOpenChatId] = useState(searchParams.get("openChat") || null);
  const { pulse, byBooking } = useNotifications();
  const lastPulseRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const [a, p, b] = await Promise.all([
        api.get("/my/bookings"),
        api.get("/my/pending-bookings").catch(() => ({ data: [] })),
        api.get("/my/rentals"),
      ]);
      setBookings(a.data);
      setPending(p.data || []);
      setRentals(b.data);
    } catch (e) {
      console.error("[MyBookings] load failed", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // V12.7: abre na aba com pendência primeiro (prioriza "Meus Anúncios" p/ não perder aluguéis).
  // Só na 1ª carga e quando não há aba explícita na URL.
  const tabAutoRef = useRef(false);
  useEffect(() => {
    if (tabAutoRef.current || tabParam) return;
    if (bookings.length === 0 && rentals.length === 0 && (pending?.length || 0) === 0) return;
    const ownerAttn = rentals.some((r) =>
      (byBooking?.[r.id] || 0) > 0 ||
      ["AGUARDANDO_ACEITE", "AGUARDANDO_CONFIRMACAO_DEVOLUCAO"].includes(r.status_locacao));
    const renterAttn = (pending?.length || 0) > 0 || bookings.some((b) =>
      (byBooking?.[b.id] || 0) > 0 || b.status_locacao === "PENDENTE_PAGAMENTO");
    if (ownerAttn) setTab("rentals");
    else if (renterAttn) setTab("bookings");
    tabAutoRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, rentals, pending, byBooking, tabParam]);

  // V7.1: Refetch automático ao voltar para a aba/app + pull-to-refresh
  // V8.5.5: foco da aba + polling 30s — bookings podem ter status atualizado pelo locador
  useFocusRefresh(load, [], { intervalMs: 30_000 });
  const ptr = usePullToRefresh(load);

  // V7.0: Auto-refresh quando o pulse do polling de notificações dispara
  useEffect(() => {
    if (pulse !== lastPulseRef.current) {
      lastPulseRef.current = pulse;
      load();
    }
  }, [pulse, load]);

  // V7.0: Escuta evento custom de deep-link (quando já está na página)
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.bookingId;
      if (id) {
        setAutoOpenChatId(id);
        // Decide a aba certa: se a reserva está em "rentals" (alugaram de mim), troca de aba
        const inRentals = rentals.some((r) => r.id === id);
        if (inRentals) setTab("rentals");
      }
    };
    window.addEventListener("aj:openChat", handler);
    return () => window.removeEventListener("aj:openChat", handler);
  }, [rentals]);

  // Detecta também via query param ao mudar de URL
  useEffect(() => {
    const id = searchParams.get("openChat");
    if (id) {
      setAutoOpenChatId(id);
      const inRentals = rentals.some((r) => r.id === id);
      if (inRentals && tab !== "rentals") setTab("rentals");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rentals.length]);

  // Limpa o autoOpenChatId após consumido (3s) para não reabrir em refresh
  useEffect(() => {
    if (!autoOpenChatId) return;
    const t = setTimeout(() => {
      setAutoOpenChatId(null);
      // remove o query param
      const next = new URLSearchParams(searchParams);
      next.delete("openChat");
      setSearchParams(next, { replace: true });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenChatId]);

  const pendingAccept = rentals.filter((r) => r.status_locacao === "AGUARDANDO_ACEITE").length;

  // V9.9 (Fase 1): "Eu Aluguei" combina pendentes de pagamento + reservas; "Meus Anúncios" = rentals
  const renterAll = [...pending, ...bookings].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const renterCounts = RENTER_FILTERS.reduce((acc, f) => { acc[f.key] = renterAll.filter((b) => renterMatch(b, f.key)).length; return acc; }, {});
  const ownerCounts = OWNER_FILTERS.reduce((acc, f) => { acc[f.key] = rentals.filter((b) => ownerMatch(b, f.key)).length; return acc; }, {});
  const renterList = renterAll.filter((b) => renterMatch(b, renterFilter));
  const ownerList = rentals.filter((b) => ownerMatch(b, ownerFilter));

  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="my-bookings-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      <h1 className="font-heading font-black text-2xl mb-4">Minhas Locações</h1>

      {user?.restricted && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="restriction-banner">
          <Warning size={20} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-800">
            <div className="font-bold">Novos aluguéis bloqueados</div>
            <p className="text-xs mt-0.5">
              {user.restricted_reason || "Sua conta está com restrições. Regularize para liberar novos aluguéis."}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 bg-slate-100 p-1 rounded-full">
        <button onClick={() => setTab("bookings")}
          className={`flex-1 px-3 py-2 rounded-full font-bold text-xs sm:text-sm transition-colors relative ${tab === "bookings" ? "bg-white text-[#004E98] shadow" : "text-slate-500"}`}
          data-testid="tab-bookings">
          Eu Aluguei ({renterAll.length})
          {pending.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center">{pending.length}</span>
          )}
        </button>
        <button onClick={() => setTab("rentals")}
          className={`flex-1 px-3 py-2 rounded-full font-bold text-xs sm:text-sm transition-colors relative ${tab === "rentals" ? "bg-white text-[#004E98] shadow" : "text-slate-500"}`}
          data-testid="tab-rentals">
          Meus Anúncios ({rentals.length})
          {pendingAccept > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#FF6B35] text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center">{pendingAccept}</span>
          )}
        </button>
      </div>

      {tab === "bookings" ? (
        <>
          <FilterChips filters={RENTER_FILTERS} active={renterFilter} onChange={setRenterFilter} counts={renterCounts} />
          {renterList.length === 0 ? (
            <div className="aj-card p-10 text-center text-slate-500" data-testid="renter-empty">
              {renterFilter === "todos" ? "Você ainda não alugou nada." : "Nenhuma locação neste filtro."}
            </div>
          ) : (
            <div className="space-y-3">
              {renterList.map((b) => (
                b.status_locacao === "PENDENTE_PAGAMENTO"
                  ? <PendingPaymentCard key={b.id} b={b} onAbandon={load} />
                  : <BookingCard key={b.id} b={b} onReload={load} autoOpenChat={autoOpenChatId === b.id} />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <FilterChips filters={OWNER_FILTERS} active={ownerFilter} onChange={setOwnerFilter} counts={ownerCounts} />
          {ownerList.length === 0 ? (
            <div className="aj-card p-10 text-center text-slate-500" data-testid="owner-empty">
              {ownerFilter === "todos" ? "Ninguém ainda alugou seus itens." : "Nenhum item neste filtro."}
            </div>
          ) : (
            <div className="space-y-3">
              {ownerList.map((b) => (
                <RentalCard key={b.id} b={b} refresh={load} autoOpenChat={autoOpenChatId === b.id} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
